import { useEffect, useState, useCallback, useRef, useMemo, useLayoutEffect, type MutableRefObject, type ReactNode } from "react";
import { useLocation, useNavigate, type NavigateFunction } from "react-router-dom";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { Stars, OrbitControls } from "@react-three/drei";
import {
  Constellation,
  DEFAULT_CONSTELLATION_APPEARANCE,
  mergeConstellationAppearance,
  type ConstellationAppearance,
  type ConstellationEdgeEntry,
  type ConstellationSceneEntry,
} from "@chuzi/shared/realms/cosmos/components";
import { Tree, ForestBackdrop } from "@chuzi/shared/realms/wilds/components";
import { cosmosMapping } from "@chuzi/shared/realms/cosmos";
import { wildsMapping } from "@chuzi/shared/realms/wilds";
import {
  computeConstellationScenePositions,
  constellationFootprintRadius,
  clampPositionToTierNebula,
  COSMOS_DEFAULT_FOOTPRINT_RADIUS,
  distributeConstellationAnchors,
  distributeClusteredConstellationAnchors,
  ClusterNebula,
  ClusterLabel,
  type ClusterAnchorResult,
} from "@chuzi/shared/realms/cosmos/components";
import { distributeForest } from "@chuzi/shared/realms/wilds/components";
import { ChuziApiError } from "@chuzi/shared/api";
import { t } from "@chuzi/shared/config";
import type {
  StoryListItem,
  SceneListItem,
  SceneMapEntry,
  UserProfile,
  RealmId,
  SceneVisibility,
  TreeGraph,
  CatalogResponse,
} from "@chuzi/shared/types";
import { resolveStoryVisibilityTier, isStoryJettisoned, isSystemStory } from "@chuzi/shared/types";
import { api } from "../lib/api";
import { getSystemBackgroundMediaId } from "../lib/system-background";
import { auth } from "../lib/auth";
import { beginOidcLogout } from "../lib/oidc";
import { useCloseAllModalsListener } from "../lib/close-all-modals";
import {
  DirectorMenu,
  type DirectorMenuAction,
} from "../components/director-menu";
import { DirectorProfileModal } from "../components/director-profile-modal";
import { StoryHoverHud } from "../components/story-hover-hud";
import { CreateInline } from "../components/create-inline";
import { CreateFab } from "../components/create-fab";
import {
  RealmScopeSelector,
  realmScopeFromPath,
  realmScopePath,
  type RealmScopeMode,
} from "../components/realm-scope-selector";
import { TagSearchBar } from "../components/tag-search-bar";
import { AllCosmosScene, buildAllPlacedStories } from "../components/all-cosmos-scene";
import {
  buildAllCosmosLayout,
  buildAllNebulaClusters,
  buildChuziNebulaLayout,
  CHUZI_CLUSTER_TAG,
  CHUZI_MINE_NEBULA_CENTER,
  CHUZI_NEBULA_COLOR,
  estimateConstellationTitleFootprintRadius,
} from "../lib/chuzi-nebula-layout";
import {
  filterStoriesByTagQuery,
  filterTagClusters,
} from "../lib/tag-nebula-layout";
import {
  pinnedCameraForMineLevel,
  vec3FromTuple,
  type CosmosWorldNavLevel,
} from "../lib/cosmos-world-mover";
import {
  cosmosBrowseAppearance,
  cosmosBrowseClusterLabelHidden,
  cosmosBrowseShowNebulaStoryLabels,
} from "../lib/cosmos-browse";
import { PinnedCosmosWorldMover } from "../components/pinned-cosmos-world-mover";
import {
  orderScenesForConstellation,
  resolveSceneStarHue,
  sortScenesForFlow,
} from "../lib/constellation-layout";
import { runWithConcurrency } from "../lib/run-with-concurrency";
import {
  CATALOG_SORT_ORDERS,
  DEFAULT_CATALOG_STORY_LIMIT,
  catalogSortLexiconKey,
  sortCatalogStories,
  type CatalogSortOrder,
} from "../lib/catalog-sort";
import { useDebouncedValue } from "../lib/use-debounced-value";
import { CosmosNavBar } from "../components/cosmos-nav-bar";
import { CosmosNavControls } from "../components/cosmos-nav-controls";
import { CosmosBreadcrumb } from "../components/cosmos-breadcrumb";
import { CosmosCommandBar } from "../components/cosmos-command-bar";
import { CosmosRadialNav } from "../components/cosmos-radial-nav";
import {
  type CosmosNavState,
  type NavContext,
  type NavLevel,
  type NavTier,
  initialNavState,
  cosmosNavReducer,
  findInitialNebulaIndex,
  getStoriesByTier,
  currentNavLabel,
  currentLevelItemCount,
  currentLevelIndex,
  canDrillDown,
  buildMineNebulaNav,
  type AllCosmosNavState,
  type AllNavContext,
  type AllNavAction,
  initialAllNavState,
  allCosmosNavReducer,
  allCurrentNavLabel,
  allCurrentLevelItemCount,
  allCurrentLevelIndex,
  allCanDrillDown,
} from "../lib/cosmos-nav-state";
import { PublishForm } from "../components/publish-form";
import { StarBirth } from "../components/star-birth";
import { StarDeath } from "../components/star-death";
import { TreeSprout } from "../components/tree-sprout";
import { TreeFall } from "../components/tree-fall";
import { SceneOrbits } from "../components/scene-orbits";
import { RealmChoiceVisual } from "../components/realm-choice-visual";
import { SceneBranches } from "../components/scene-branches";
import { SceneEditor } from "../components/scene-editor";
import { StoryViewer } from "../components/story-viewer";
import { GeneratingEffect } from "../components/generating-effect";
import { StarFocusGlow } from "../components/star-focus-glow";
import { DirectorAvatar } from "../components/director-avatar";
import { buildConstellationScenePreview } from "../lib/constellation-scene-data";
import { ConstellationScenePreviewCard } from "../components/constellation-scene-preview";
import { editorVisibleScenes } from "../lib/story-scaffold";
import * as THREE from "three";

type PlacedScene = {
  story: StoryListItem;
  scene: SceneListItem;
  visual: ReturnType<typeof cosmosMapping> & { position: [number, number, number] };
  visibility: SceneVisibility;
};

type CreatingState =
  | { phase: "idle" }
  | { phase: "animating" }
  | { phase: "form" }
  | { phase: "scenes"; storyId: string };

type DeletingState =
  | { phase: "idle" }
  | { phase: "dying"; storyId: string; mode: "jettison" | "purge" };

type EditingState =
  | { phase: "idle" }
  | { phase: "editing"; storyId: string; sceneIndex: number };

type ViewingState =
  | { phase: "idle" }
  | { phase: "viewing"; storyId: string };

/**
 * Attempt to pull a StoryListItem[] out of whatever the API returns.
 * Handles: { data: [...] }, [...], and any object whose first array-valued
 * property contains objects with an `id` field.
 */
function extractStories(res: unknown): StoryListItem[] {
  if (Array.isArray(res)) return res;
  if (res != null && typeof res === "object") {
    const obj = res as Record<string, unknown>;
    if (Array.isArray(obj.stories)) return obj.stories as StoryListItem[];
    if (Array.isArray(obj.data)) return obj.data;
    for (const key of Object.keys(obj)) {
      const val = obj[key];
      if (
        Array.isArray(val) &&
        (val.length === 0 || (val[0] != null && typeof val[0] === "object" && "id" in val[0]))
      ) {
        console.warn(
          `[extractStories] stories found under key "${key}" instead of "data"`,
        );
        return val;
      }
    }
  }
  console.warn("[extractStories] could not locate stories array in response", res);
  return [];
}

function resolveStoryOwnerId(story: StoryListItem): string | null {
  if (story.creator?.id) return story.creator.id;
  const raw = story as StoryListItem & { user_id?: unknown; user?: { id?: unknown } };
  if (typeof raw.user_id === "string") return raw.user_id;
  if (raw.user && typeof raw.user.id === "string") return raw.user.id;
  return null;
}

/** Scenes must be fetched; multi-scene stories also need tree_graph before layout is stable. */
function isStoryCosmosLayoutReady(
  story: StoryListItem,
  scenes: Record<string, SceneListItem[]>,
  storyGraphs: Record<string, TreeGraph>,
): boolean {
  const storyScenes = scenes[story.id];
  if (!storyScenes) return false;
  if (story.is_system || storyScenes.length <= 1) return true;
  return (storyGraphs[story.id]?.nodes?.length ?? 0) > 0;
}

function areStoriesCosmosLayoutReady(
  stories: StoryListItem[],
  scenes: Record<string, SceneListItem[]>,
  storyGraphs: Record<string, TreeGraph>,
): boolean {
  if (stories.length === 0) return true;
  return stories.every((story) =>
    isStoryCosmosLayoutReady(story, scenes, storyGraphs),
  );
}

function computeStorySceneVisibility(
  story: StoryListItem,
  storyScenes: SceneListItem[],
  user: UserProfile | null,
): SceneVisibility[] {
  const isCreator = Boolean(
    user && resolveStoryOwnerId(story) === user.id,
  );
  const isAdminSystemEditor = Boolean(user?.is_admin && story.is_system);
  if (isCreator || isAdminSystemEditor) {
    return storyScenes.map(() => ({ navigable: true, dimmed: false }));
  }

  const titleIndex = storyScenes.findIndex((scene) => scene.is_title);
  const unlockedIndex = titleIndex >= 0 ? titleIndex : 0;

  return storyScenes.map((_, idx) => ({
    navigable: idx === unlockedIndex,
    dimmed: idx !== unlockedIndex,
  }));
}

const NEW_STORY_ANCHOR_ID = "__new-story__";

const JETTISON_NEBULA_CENTER: [number, number, number] = [96, -18, -40];
const JETTISON_NEBULA_RADIUS = 28;
const JETTISON_NEBULA_CLUSTER = {
  center: JETTISON_NEBULA_CENTER,
  radius: JETTISON_NEBULA_RADIUS,
};

function distributeJettisonedAnchors(
  stories: StoryListItem[],
): Map<string, [number, number, number]> {
  const anchors = new Map<string, [number, number, number]>();
  const [cx, cy, cz] = JETTISON_NEBULA_CENTER;

  stories.forEach((story, i) => {
    const seed = hashStringToFloat(story.id);
    const angle = seed * Math.PI * 2 + i * 2.39996;
    const r = (0.3 + seed * 0.7) * (JETTISON_NEBULA_RADIUS - 3);
    const yJitter = (hashStringToFloat(story.id + "y") - 0.5) * 8;
    const zJitter = (hashStringToFloat(story.id + "z") - 0.5) * 12;
    anchors.set(story.id, [
      cx + Math.cos(angle) * r,
      cy + yJitter,
      cz + Math.sin(angle) * r + zJitter,
    ]);
  });
  return anchors;
}

function hashStringToFloat(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return (Math.abs(h) % 10000) / 10000;
}

function buildCosmosAnchorInputs(
  stories: StoryListItem[],
  scenes: Record<string, SceneListItem[]>,
  storyGraphs: Record<string, TreeGraph>,
) {
  const sortedStories = [...stories].sort((a, b) =>
    a.created_at.localeCompare(b.created_at),
  );

  return sortedStories.map((story) => {
    const storyScenes = scenes[story.id];
    const graph = storyGraphs[story.id];
    let footprintRadius = COSMOS_DEFAULT_FOOTPRINT_RADIUS;

    if (storyScenes?.length) {
      const displayScenes = orderScenesForConstellation(storyScenes, graph);
      const localPositions = computeConstellationScenePositions(displayScenes, graph);
      footprintRadius = constellationFootprintRadius(localPositions);
    }

    return { id: story.id, footprintRadius };
  });
}

function computeNewStoryCreationAnchor(
  realm: RealmId,
  stories: StoryListItem[] | null,
  scenes: Record<string, SceneListItem[]>,
  storyGraphs: Record<string, TreeGraph>,
): [number, number, number] {
  if (!Array.isArray(stories)) return [0, 0, 0];

  if (realm === "wilds") {
    const count = Math.max(1, stories.length + 1);
    const positions = distributeForest(count, { seed: 42, radius: 18 });
    return positions[count - 1] as [number, number, number];
  }

  const anchorInputs = buildCosmosAnchorInputs(stories, scenes, storyGraphs);
  const clusteredInputs = [
    ...anchorInputs.map((input) => ({
      ...input,
      tier: resolveStoryVisibilityTier(
        stories.find((s) => s.id === input.id) ?? { published: false, soft_published: false },
      ),
    })),
    { id: NEW_STORY_ANCHOR_ID, footprintRadius: COSMOS_DEFAULT_FOOTPRINT_RADIUS, tier: "private" as const },
  ];
  const result = distributeClusteredConstellationAnchors(clusteredInputs);
  return result.anchors.get(NEW_STORY_ANCHOR_ID) ?? [0, 0, 0];
}

export function MyRealmPage() {
  const [stories, setStories] = useState<StoryListItem[] | null>(null);
  const [scenes, setScenes] = useState<Record<string, SceneListItem[]>>({});
  const [storyGraphs, setStoryGraphs] = useState<Record<string, TreeGraph>>({});
  const [sceneMaps, setSceneMaps] = useState<Record<string, Record<string, SceneMapEntry>>>({});
  const [coverboxes, setCoverboxes] = useState<Record<string, string | null>>({});
  const [user, setUser] = useState<UserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [cosmosNav, setCosmosNav] = useState<CosmosNavState>(initialNavState(0));
  const cosmosNavInitialized = useRef(false);
  const promoteSyncRef = useRef(false);
  const [allCosmosNav, setAllCosmosNav] = useState<AllCosmosNavState>(initialAllNavState(0));
  const [allNavSearchValue, setAllNavSearchValue] = useState("");
  const [allNavSearchSuggestions, setAllNavSearchSuggestions] = useState<string[]>([]);
  const [creating, setCreating] = useState<CreatingState>({ phase: "idle" });
  const [creationAnchor, setCreationAnchor] = useState<[number, number, number]>([0, 0, 0]);
  const [deleting, setDeleting] = useState<DeletingState>({ phase: "idle" });
  const [editing, setEditing] = useState<EditingState>({ phase: "idle" });
  const [viewing, setViewing] = useState<ViewingState>({ phase: "idle" });
  const [generatingSceneIds, setGeneratingSceneIds] = useState<Set<string>>(new Set());
  const [openModal, setOpenModal] = useState<"profile" | null>(null);
  const [tagQuery, setTagQuery] = useState("");
  const [catalogSortOrder, setCatalogSortOrder] = useState<CatalogSortOrder>("newest");
  const [catalogFull, setCatalogFull] = useState<CatalogResponse | null>(null);
  const [catalogFiltered, setCatalogFiltered] = useState<CatalogResponse | null>(null);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogFilterLoading, setCatalogFilterLoading] = useState(false);
  const [storySettingsStoryId, setStorySettingsStoryId] = useState<string | null>(null);
  const [storySettingsMode, setStorySettingsMode] = useState<"publish" | "metadata">("metadata");
  const [hudScreen, setHudScreen] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [spaceAnchor, setSpaceAnchor] = useState<[number, number, number] | null>(
    null,
  );
  const [recenterKey, setRecenterKey] = useState(0);
  const [scenePositionOverrides, setScenePositionOverrides] = useState<
    Record<string, [number, number, number]>
  >({});
  const [rearrangingSceneId, setRearrangingSceneId] = useState<string | null>(null);
  const sceneBasePositionsRef = useRef<Map<string, [number, number, number]>>(new Map());
  const location = useLocation();
  const navigate = useNavigate();
  const navigateRef = useRef<NavigateFunction>(navigate);
  navigateRef.current = navigate;
  const scopeMode = realmScopeFromPath(location.pathname);
  const prevScopeModeRef = useRef<RealmScopeMode | null>(null);

  useCloseAllModalsListener(
    useCallback(() => {
      setOpenModal(null);
      setStorySettingsStoryId(null);
      setEditing({ phase: "idle" });
    }, []),
  );

  const mountId = useRef(Math.random().toString(36).slice(2, 6));
  const log = useCallback(
    (tag: string, ...args: unknown[]) =>
      console.log(`[MyRealm:${mountId.current}] ${tag}`, ...args),
    [],
  );

  const realm: RealmId = user?.realm === "wilds" ? "wilds" : "cosmos";
  const debouncedTagQuery = useDebouncedValue(tagQuery, 300);

  const refreshStoryGraphTimerRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  function refreshStoryGraph(storyId: string, immediate = false) {
    const pending = refreshStoryGraphTimerRef.current[storyId];
    if (pending) clearTimeout(pending);
    const run = () => {
      delete refreshStoryGraphTimerRef.current[storyId];
      api.watch.sceneMap(storyId).then((map) => {
        if (map.tree_graph) {
          setStoryGraphs((prev) => ({ ...prev, [storyId]: map.tree_graph }));
        }
        if (map.scene_map) {
          setSceneMaps((prev) => ({ ...prev, [storyId]: map.scene_map }));
        }
      }).catch(() => {});
    };
    if (immediate) {
      run();
      return;
    }
    refreshStoryGraphTimerRef.current[storyId] = setTimeout(run, 2500);
  }

  function fetchScenesForStories(storyList: StoryListItem[]) {
    const tasks = storyList.map((story) => async () => {
      try {
        const sceneList = await api.scenes.index(story.id);
        setScenes((prev) => ({ ...prev, [story.id]: sortScenesForFlow(sceneList) }));
      } catch (err: unknown) {
        log("fetch:scenes", "ERROR for story", story.id, err);
      }
      refreshStoryGraph(story.id, true);
    });
    void runWithConcurrency(tasks, 4);
  }

  useEffect(() => {
    log("mount-effect", { authenticated: auth.isAuthenticated() });
    if (!auth.isAuthenticated()) {
      navigateRef.current("/login", { replace: true });
      return;
    }
    let cancelled = false;

    log("fetch:mine", "started");
    api.stories
      .mine()
      .then((res: any) => {
        const keys = res != null && typeof res === "object" ? Object.keys(res) : [];
        log("fetch:mine", "resolved — keys:", keys.join(", "), "| cancelled:", cancelled);
        if (cancelled) return;
        const data = extractStories(res);
        log("fetch:mine", "setStories", { count: data.length, ids: data.map((s) => s.id) });
        setStories(data);
        if (res?.meta?.coverboxes) {
          setCoverboxes(res.meta.coverboxes);
        }
        if (data.length > 0) setFocusedIndex(0);
        fetchScenesForStories(data);
      })
      .catch((err: unknown) => {
        log("fetch:mine", "ERROR", err);
        if (cancelled) return;
        if (err instanceof ChuziApiError && err.status === 401) {
          auth.clearToken();
          navigateRef.current("/login", { replace: true });
          return;
        }
        setError(
          err instanceof Error ? err.message : "Failed to load your stories"
        );
      });

    api.auth
      .user()
      .then((u: UserProfile) => {
        log("fetch:user", "resolved", { cancelled, realm: u?.realm });
        if (!cancelled) setUser(u);
      })
      .catch((err: unknown) => {
        log("fetch:user", "ERROR", err);
      });

    return () => {
      log("mount-effect", "cleanup — cancelled");
      cancelled = true;
    };
  }, [log]);

  useEffect(() => {
    const prev = prevScopeModeRef.current;
    if (prev === scopeMode) return;
    prevScopeModeRef.current = scopeMode;

    setFocusedIndex(0);
    setSpaceAnchor(null);
    setTagQuery("");
    setCatalogFiltered(null);
    if (scopeMode === "mine") {
      setCatalogFull(null);
      setCatalogLoading(false);
      setCatalogError(null);
    }
  }, [scopeMode]);

  useEffect(() => {
    if (scopeMode !== "all" || catalogFull) return;
    let cancelled = false;
    setCatalogLoading(true);
    api.catalog
      .index()
      .then((res) => {
        if (!cancelled) {
          setCatalogFull(res);
          setCatalogError(null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ChuziApiError && err.status === 401) {
          auth.clearToken();
          navigateRef.current("/login", { replace: true });
          return;
        }
        setCatalogError(err instanceof Error ? err.message : "Failed to load catalog");
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scopeMode, catalogFull]);

  useEffect(() => {
    if (scopeMode !== "all" || !catalogFull) return;
    const q = debouncedTagQuery.trim();
    if (!q) {
      setCatalogFiltered(null);
      setCatalogFilterLoading(false);
      return;
    }

    let cancelled = false;
    setCatalogFilterLoading(true);
    api.catalog
      .index({ tag: q })
      .then((res) => {
        if (!cancelled) {
          setCatalogFiltered(res);
          setCatalogError(null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ChuziApiError && err.status === 401) {
          auth.clearToken();
          navigateRef.current("/login", { replace: true });
          return;
        }
        setCatalogError(err instanceof Error ? err.message : "Failed to filter catalog");
      })
      .finally(() => {
        if (!cancelled) setCatalogFilterLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scopeMode, catalogFull, debouncedTagQuery]);

  useEffect(() => {
    if (scopeMode === "all") {
      setFocusedIndex(0);
    }
  }, [debouncedTagQuery, catalogSortOrder, scopeMode]);

  useEffect(() => {
    if (scopeMode !== "mine") return;
    setFocusedIndex(0);
    setSpaceAnchor(null);
  }, [debouncedTagQuery, scopeMode]);

  const minePlacementStories = useMemo(() => {
    if (!Array.isArray(stories)) return [];
    const systemStories = user?.is_admin
      ? stories.filter((story) => story.is_system && !isStoryJettisoned(story))
      : [];
    const live = stories.filter((story) => !isStoryJettisoned(story) && !story.is_system);
    const tagged = !debouncedTagQuery.trim()
      ? live
      : filterStoriesByTagQuery(live, debouncedTagQuery);
    return [...systemStories, ...tagged];
  }, [stories, debouncedTagQuery, user?.is_admin]);

  const jettisonedPlacementStories = useMemo(() => {
    if (!Array.isArray(stories)) return [];
    return stories.filter(isStoryJettisoned);
  }, [stories]);

  const cosmosLayoutReady = useMemo(
    () =>
      realm !== "cosmos" ||
      areStoriesCosmosLayoutReady(minePlacementStories, scenes, storyGraphs),
    [realm, minePlacementStories, scenes, storyGraphs],
  );

  const catalogFullStories = catalogFull?.data ?? [];
  const catalogSystemStories = useMemo(
    () => catalogFullStories.filter((story) => story.is_system),
    [catalogFullStories],
  );
  const visibleCatalogStories = debouncedTagQuery.trim()
    ? (catalogFiltered?.data ?? [])
    : catalogFullStories;
  const userCatalogStories = useMemo(
    () => visibleCatalogStories.filter((story) => !story.is_system),
    [visibleCatalogStories],
  );
  const sortedUserCatalogStories = useMemo(
    () => sortCatalogStories(userCatalogStories, catalogSortOrder),
    [userCatalogStories, catalogSortOrder],
  );
  const limitedUserCatalogStories = useMemo(
    () => sortedUserCatalogStories.slice(0, DEFAULT_CATALOG_STORY_LIMIT),
    [sortedUserCatalogStories],
  );
  const allCatalogStories = useMemo(
    () => [...catalogSystemStories, ...limitedUserCatalogStories],
    [catalogSystemStories, limitedUserCatalogStories],
  );

  const allCosmosLayoutReady = useMemo(
    () => areStoriesCosmosLayoutReady(allCatalogStories, scenes, storyGraphs),
    [allCatalogStories, scenes, storyGraphs],
  );

  const cosmosClusterResult = useMemo((): ClusterAnchorResult | null => {
    if (
      realm !== "cosmos" ||
      !Array.isArray(stories) ||
      stories.length === 0 ||
      !cosmosLayoutReady
    ) {
      return null;
    }

    const liveStories = stories.filter((s) => !isStoryJettisoned(s) && !s.is_system);
    const anchorInputs = buildCosmosAnchorInputs(liveStories, scenes, storyGraphs);
    const clusteredInputs = anchorInputs.map((input) => ({
      ...input,
      tier: resolveStoryVisibilityTier(
        liveStories.find((s) => s.id === input.id) ?? { published: false, soft_published: false },
      ),
    }));
    return distributeClusteredConstellationAnchors(clusteredInputs);
  }, [realm, stories, scenes, storyGraphs, cosmosLayoutReady]);

  const adminSystemStories = useMemo(() => {
    if (!user?.is_admin || !Array.isArray(stories)) return [];
    return stories.filter((story) => story.is_system && !isStoryJettisoned(story));
  }, [user?.is_admin, stories]);

  const hasChuziNebula = adminSystemStories.length > 0;
  const mineNebulaNav = useMemo(
    () => buildMineNebulaNav(hasChuziNebula),
    [hasChuziNebula],
  );
  const chuziNebulaLabel = t(realm, "nebula_chuzi", "CHUZI!");

  const adminChuziLayout = useMemo(() => {
    if (adminSystemStories.length === 0) return null;
    const footprints = new Map<string, number>();
    for (const story of adminSystemStories) {
      let footprint = estimateConstellationTitleFootprintRadius(story.title);
      const storyScenes = scenes[story.id];
      const graph = storyGraphs[story.id];
      if (storyScenes?.length) {
        const displayScenes = orderScenesForConstellation(
          editorVisibleScenes(storyScenes),
          graph,
        );
        const localPositions = computeConstellationScenePositions(displayScenes, graph);
        footprint = Math.max(
          footprint,
          constellationFootprintRadius(localPositions),
        );
      }
      footprints.set(story.id, footprint);
    }
    return buildChuziNebulaLayout(adminSystemStories, {
      center: CHUZI_MINE_NEBULA_CENTER,
      footprintByStoryId: footprints,
    });
  }, [adminSystemStories, scenes, storyGraphs]);

  const cosmosStoryAnchors = useMemo(() => {
    const anchors = new Map<string, [number, number, number]>(
      cosmosClusterResult?.anchors ?? [],
    );
    if (adminChuziLayout) {
      for (const [storyId, position] of adminChuziLayout.storyPositions) {
        anchors.set(storyId, position);
      }
    }
    return anchors;
  }, [cosmosClusterResult, adminChuziLayout]);

  const jettisonedAnchors = useMemo(() => {
    if (realm !== "cosmos") return new Map<string, [number, number, number]>();
    return distributeJettisonedAnchors(jettisonedPlacementStories);
  }, [realm, jettisonedPlacementStories]);

  const activeCatalogMeta =
    debouncedTagQuery.trim() && catalogFiltered
      ? catalogFiltered.meta
      : catalogFull?.meta;
  const allCosmosLayout = useMemo(() => {
    if (!catalogFull || !allCosmosLayoutReady) return null;
    if (catalogSystemStories.length === 0 && limitedUserCatalogStories.length === 0) {
      return null;
    }
    const footprints = new Map<string, number>();
    for (const story of allCatalogStories) {
      let footprint = estimateConstellationTitleFootprintRadius(story.title);
      const storyScenes = scenes[story.id];
      const graph = storyGraphs[story.id];
      if (storyScenes?.length) {
        const displayScenes = orderScenesForConstellation(
          editorVisibleScenes(storyScenes),
          graph,
        );
        const localPositions = computeConstellationScenePositions(displayScenes, graph);
        footprint = Math.max(footprint, constellationFootprintRadius(localPositions));
      }
      footprints.set(story.id, footprint);
    }
    return buildAllCosmosLayout(catalogSystemStories, limitedUserCatalogStories, footprints);
  }, [
    catalogFull,
    allCatalogStories,
    catalogSystemStories,
    limitedUserCatalogStories,
    scenes,
    storyGraphs,
    allCosmosLayoutReady,
  ]);
  const allPlaced = useMemo(() => {
    if (!allCosmosLayout) return [];
    return buildAllPlacedStories(allCosmosLayout.allStories, {
      clusters: allCosmosLayout.tagLayout.clusters,
      storyPositions: allCosmosLayout.storyPositions,
    });
  }, [allCosmosLayout]);

  useEffect(() => {
    if (scopeMode !== "all" || !catalogFull) return;
    const storiesToFetch =
      allCatalogStories.length > 0 ? allCatalogStories : catalogSystemStories;
    if (storiesToFetch.length === 0) return;
    fetchScenesForStories(storiesToFetch);
  }, [scopeMode, catalogFull, allCatalogStories, catalogSystemStories]);

  const placed: PlacedScene[] = useMemo(() => {
    if (!Array.isArray(stories) || stories.length === 0) return [];
    const mapping = realm === "wilds" ? wildsMapping : cosmosMapping;
    const wildsStoryPositions =
      realm === "wilds"
        ? distributeForest(stories.length, { seed: 42, radius: 18 })
        : null;
    const storyIndexById = new Map(stories.map((story, index) => [story.id, index]));

    const result: PlacedScene[] = [];

    minePlacementStories.forEach((story) => {
      const storyIdx = storyIndexById.get(story.id) ?? 0;
      const anchor =
        realm === "cosmos"
          ? (cosmosStoryAnchors.get(story.id) ?? [0, 0, 0])
          : (wildsStoryPositions![storyIdx] as [number, number, number]);
      const storyScenes = scenes[story.id];

      if (!storyScenes || storyScenes.length === 0) {
        const baseVisual = mapping(story);
        result.push({
          story,
          scene: {
            id: `${story.id}-placeholder`,
            story_id: story.id,
            title: story.title,
            order: 0,
            is_title: true,
            is_end: false,
            color: null,
            media_id: null,
            alt_media_id: null,
            media_mode: null,
            text_content: null,
            text_style: null,
            created_at: story.created_at,
            updated_at: story.updated_at,
          },
          visual: { ...baseVisual, position: anchor },
          visibility: { navigable: true, dimmed: false },
        });
        return;
      }

      const graph = storyGraphs[story.id];
      const visibleScenes = editorVisibleScenes(storyScenes);
      const displayScenes = orderScenesForConstellation(visibleScenes, graph);
      const baseVisual = mapping(story);
      const vis = computeStorySceneVisibility(story, displayScenes, user);
      const storyTier =
        realm === "cosmos" && !isSystemStory(story)
          ? resolveStoryVisibilityTier(story)
          : undefined;
      const scenePositions = computeConstellationScenePositions(
        displayScenes,
        graph,
        anchor,
        storyTier,
      );

      displayScenes.forEach((scene, sceneIdx) => {
        const basePosition = scenePositions.get(scene.id) ?? anchor;
        sceneBasePositionsRef.current.set(scene.id, basePosition);
        const savedOffset = isSystemStory(story) ? null : scene.layout_offset;
        const position: [number, number, number] =
          scenePositionOverrides[scene.id] ?? [
            basePosition[0] + (savedOffset?.x ?? 0),
            basePosition[1] + (savedOffset?.y ?? 0),
            basePosition[2] + (savedOffset?.z ?? 0),
          ];

        let scaleMultiplier = 1;
        if (scene.is_title) {
          scaleMultiplier = 1.15;
        }

        const hue = resolveSceneStarHue(scene, baseVisual.hue);

        result.push({
          story,
          scene,
          visual: {
            ...baseVisual,
            position,
            hue,
            scale: baseVisual.scale * scaleMultiplier,
          },
          visibility: vis[sceneIdx],
        });
      });
    });

    // Jettisoned stories: dimmed, navigable, placed in their own z-offset cluster
    if (realm === "cosmos") {
      jettisonedPlacementStories.forEach((story) => {
        const anchor = jettisonedAnchors.get(story.id) ?? JETTISON_NEBULA_CENTER;
        const baseVisual = mapping(story);
        const storyScenes = scenes[story.id];

        if (!storyScenes || storyScenes.length === 0) {
          result.push({
            story,
            scene: {
              id: `${story.id}-placeholder`,
              story_id: story.id,
              title: story.title,
              order: 0,
              is_title: true,
              is_end: false,
              color: null,
              media_id: null,
              alt_media_id: null,
              media_mode: null,
              text_content: null,
              text_style: null,
              created_at: story.created_at,
              updated_at: story.updated_at,
            },
            visual: { ...baseVisual, position: anchor, intensity: 0.3, scale: baseVisual.scale * 0.7 },
            visibility: { navigable: true, dimmed: true },
          });
          return;
        }

        const graph = storyGraphs[story.id];
        const visibleScenes = editorVisibleScenes(storyScenes);
        const displayScenes = orderScenesForConstellation(visibleScenes, graph);
        const scenePositions = computeConstellationScenePositions(
          displayScenes,
          graph,
          anchor,
        );

        displayScenes.forEach((scene) => {
          const position = scenePositions.get(scene.id) ?? anchor;
          const hue = resolveSceneStarHue(scene, baseVisual.hue);
          result.push({
            story,
            scene,
            visual: {
              ...baseVisual,
              position,
              hue,
              intensity: 0.3,
              scale: baseVisual.scale * 0.7,
            },
            visibility: { navigable: true, dimmed: true },
          });
        });
      });
    }

    return result;
  }, [stories, minePlacementStories, jettisonedPlacementStories, scenes, storyGraphs, realm, user, cosmosStoryAnchors, jettisonedAnchors, scenePositionOverrides]);

  const storyStartIndices = useMemo(() => {
    if (!minePlacementStories.length || !placed.length) return [] as number[];
    return minePlacementStories
      .map((story) => {
        const firstNavigable = placed.findIndex(
          (item) => item.story.id === story.id && item.visibility.navigable,
        );
        if (firstNavigable >= 0) return firstNavigable;
        return placed.findIndex((item) => item.story.id === story.id);
      })
      .filter((idx): idx is number => idx >= 0);
  }, [minePlacementStories, placed]);

  const focusedStoryIndex = useMemo(() => {
    if (!storyStartIndices.length) return 0;
    for (let s = storyStartIndices.length - 1; s >= 0; s--) {
      if (focusedIndex >= storyStartIndices[s]) return s;
    }
    return 0;
  }, [focusedIndex, storyStartIndices]);

  const storiesByTier = useMemo(
    () => getStoriesByTier(stories, { includeChuziTier: hasChuziNebula }),
    [stories, hasChuziNebula],
  );

  const nebulaLabels = useMemo(() => {
    const tierLabels = [
      t(realm, "cosmos_cluster_unpublished", "Shipyard Nebula"),
      t(realm, "cosmos_cluster_limited", "Relay Nebula"),
      t(realm, "cosmos_cluster_published", "Broadcast Nebula"),
      t(realm, "cosmos_cluster_jettisoned", "Jettison Nebula"),
    ];
    return hasChuziNebula ? [chuziNebulaLabel, ...tierLabels] : tierLabels;
  }, [realm, hasChuziNebula, chuziNebulaLabel]);

  const navContext: NavContext = useMemo(() => {
    const tier = mineNebulaNav.nebulaTiers[cosmosNav.nebulaIndex];
    const storiesInTier = tier ? (storiesByTier[tier] ?? []) : [];
    const story = storiesInTier[cosmosNav.storyIndex];
    const storyScenes = story ? (scenes[story.id] ?? []) : [];
    const currentScene = storyScenes[cosmosNav.sceneIndex];
    const choicesForScene = currentScene
      ? getOutgoingChoiceIds(storyGraphs[currentScene.story_id], currentScene.id)
      : [];
    return {
      storiesByTier,
      nebulaTiers: mineNebulaNav.nebulaTiers,
      nebulaSpatialNavOrder: mineNebulaNav.nebulaSpatialNavOrder,
      scenesByStory: scenes,
      choicesForScene,
      nebulaLabels,
      realm,
    };
  }, [storiesByTier, mineNebulaNav, scenes, storyGraphs, cosmosNav.nebulaIndex, cosmosNav.storyIndex, cosmosNav.sceneIndex, nebulaLabels, realm]);

  useEffect(() => {
    if (!stories?.length) return;
    if (!cosmosNavInitialized.current) {
      cosmosNavInitialized.current = true;
      const idx = findInitialNebulaIndex(stories, mineNebulaNav.nebulaTiers);
      setCosmosNav(initialNavState(idx));
      return;
    }
    setCosmosNav((prev) => {
      if (prev.level === "nebula") return prev;
      const tier = mineNebulaNav.nebulaTiers[prev.nebulaIndex];
      const storiesInTier = tier ? (storiesByTier[tier] ?? []) : [];
      if (prev.level === "story" && prev.storyIndex >= storiesInTier.length) {
        return initialNavState(prev.nebulaIndex);
      }
      if (prev.level === "scene") {
        const story = storiesInTier[prev.storyIndex];
        if (!story || !(scenes[story.id]?.length)) {
          return initialNavState(prev.nebulaIndex);
        }
      }
      return prev;
    });
  }, [stories, storiesByTier, scenes, mineNebulaNav.nebulaTiers]);

  const dispatchNav = useCallback(
    (action: Parameters<typeof cosmosNavReducer>[1]) => {
      setCosmosNav((prev) => {
        const next = cosmosNavReducer(prev, action, navContext);
        if (next === prev) return prev;
        syncFocusedIndexFromNav(next);
        return next;
      });
    },
    [navContext],
  );

  const syncFocusedIndexFromNav = useCallback(
    (nav: CosmosNavState) => {
      if (scopeMode !== "mine" || realm !== "cosmos") return;
      const tier = mineNebulaNav.nebulaTiers[nav.nebulaIndex];
      const storiesInTier = tier ? (storiesByTier[tier] ?? []) : [];
      if (nav.level === "nebula") {
        if (storiesInTier.length > 0) {
          const firstStory = storiesInTier[0];
          const idx = placed.findIndex(
            (p) => p.story.id === firstStory.id && p.visibility.navigable,
          );
          if (idx >= 0) setFocusedIndex(idx);
        }
        return;
      }
      const story = storiesInTier[nav.storyIndex];
      if (!story) return;
      if (nav.level === "story") {
        const idx = placed.findIndex(
          (p) => p.story.id === story.id && p.visibility.navigable,
        );
        if (idx >= 0) setFocusedIndex(idx);
        return;
      }
      const storyScenes = scenes[story.id] ?? [];
      const targetScene = storyScenes[nav.sceneIndex];
      if (!targetScene) return;
      const idx = placed.findIndex((p) => p.scene.id === targetScene.id);
      if (idx >= 0) setFocusedIndex(idx);
    },
    [scopeMode, realm, storiesByTier, placed, scenes],
  );

  useLayoutEffect(() => {
    if (!promoteSyncRef.current) return;
    if (scopeMode !== "mine" || realm !== "cosmos") return;
    syncFocusedIndexFromNav(cosmosNav);
    promoteSyncRef.current = false;
  }, [stories, placed, cosmosNav, scopeMode, realm, syncFocusedIndexFromNav]);

  // --- ALL-mode nav context & dispatch ---

  const allNavContext: AllNavContext = useMemo(() => {
    if (!allCosmosLayout) {
      return {
        nebulaClusters: [],
        chuziNebulaLabel,
        storiesByChuzi: [],
        storiesByTag: {},
        scenesByStory: scenes,
        realm,
      };
    }
    const storiesByTag: Record<string, StoryListItem[]> = {};
    const filteredTagClusters = filterTagClusters(
      allCosmosLayout.tagLayout.clusters,
      debouncedTagQuery,
    );
    for (const cluster of filteredTagClusters) {
      storiesByTag[cluster.tag] = limitedUserCatalogStories.filter((story) =>
        cluster.storyIds.includes(story.id),
      );
    }
    const storiesByChuzi = catalogSystemStories.filter((story) =>
      allCosmosLayout.chuziCluster.storyIds.includes(story.id),
    );
    return {
      nebulaClusters: buildAllNebulaClusters(
        allCosmosLayout.chuziCluster,
        filteredTagClusters,
      ),
      chuziNebulaLabel,
      storiesByChuzi,
      storiesByTag,
      scenesByStory: scenes,
      realm,
    };
  }, [
    allCosmosLayout,
    limitedUserCatalogStories,
    catalogSystemStories,
    debouncedTagQuery,
    scenes,
    realm,
    chuziNebulaLabel,
  ]);

  const syncAllFocusedIndex = useCallback(
    (nav: AllCosmosNavState) => {
      if (scopeMode !== "all" || !allCosmosLayout) return;
      const cluster = allNavContext.nebulaClusters[nav.nebulaIndex];
      if (!cluster) return;
      if (nav.level === "nebula") {
        const firstStoryId = cluster.storyIds[0];
        if (firstStoryId) {
          const idx = allPlaced.findIndex((p) => p.story.id === firstStoryId);
          if (idx >= 0) setFocusedIndex(idx);
        }
        return;
      }
      const stories =
        cluster.tag === CHUZI_CLUSTER_TAG
          ? allNavContext.storiesByChuzi
          : (allNavContext.storiesByTag[cluster.tag] ?? []);
      const story = stories[nav.storyIndex];
      if (!story) return;
      const idx = allPlaced.findIndex((p) => p.story.id === story.id);
      if (idx >= 0) setFocusedIndex(idx);
    },
    [scopeMode, allCosmosLayout, allNavContext, allPlaced],
  );

  const dispatchAllNav = useCallback(
    (action: AllNavAction) => {
      setAllCosmosNav((prev) => {
        const next = allCosmosNavReducer(prev, action, allNavContext);
        if (next === prev) return prev;
        syncAllFocusedIndex(next);
        return next;
      });
    },
    [allNavContext, syncAllFocusedIndex],
  );

  useEffect(() => {
    if (scopeMode !== "all" || !allCosmosLayout) return;
    setAllCosmosNav(initialAllNavState(0));
  }, [scopeMode, allCosmosLayout]);

  useEffect(() => {
    let cancelled = false;
    const q = allNavSearchValue.trim();
    if (!q) {
      setAllNavSearchSuggestions([]);
      return;
    }
    api.tags
      .index({ q })
      .then((res) => {
        if (!cancelled) setAllNavSearchSuggestions(res.data);
      })
      .catch(() => {
        if (!cancelled) setAllNavSearchSuggestions([]);
      });
    return () => { cancelled = true; };
  }, [allNavSearchValue]);

  function handleAllNavSearchSelect(tag: string) {
    const idx = allNavContext.nebulaClusters.findIndex(
      (cluster) =>
        cluster.tag !== CHUZI_CLUSTER_TAG &&
        cluster.tag.toLowerCase() === tag.toLowerCase(),
    );
    if (idx >= 0) {
      dispatchAllNav({ type: "JUMP_TO_NEBULA", index: idx });
    } else {
      setTagQuery(tag);
    }
    setAllNavSearchValue("");
  }

  const allFocusedStoryId = useMemo(() => {
    if (scopeMode !== "all" || !allCosmosLayout) return null;
    const cluster = allNavContext.nebulaClusters[allCosmosNav.nebulaIndex];
    if (!cluster) return null;
    if (allCosmosNav.level === "nebula") {
      return cluster.storyIds[0] ?? null;
    }
    const stories =
      cluster.tag === CHUZI_CLUSTER_TAG
        ? allNavContext.storiesByChuzi
        : (allNavContext.storiesByTag[cluster.tag] ?? []);
    return stories[allCosmosNav.storyIndex]?.id ?? null;
  }, [scopeMode, allCosmosLayout, allNavContext, allCosmosNav]);

  function handleStorySelect(storyIdx: number) {
    const sceneIdx = storyStartIndices[storyIdx];
    if (sceneIdx != null) {
      setSpaceAnchor(null);
      setFocusedIndex(sceneIdx);
    }
  }

  const handleSceneSelect = useCallback((index: number) => {
    setSpaceAnchor(null);

    if (realm === "cosmos" && scopeMode === "mine") {
      const clickedItem = placed[index];
      if (!clickedItem) return;

      if (index === focusedIndex) {
        if (cosmosNav.level === "nebula" || cosmosNav.level === "story") {
          dispatchNav({ type: "DOWN" });
          return;
        }
        if (cosmosNav.level === "scene") {
          const storyId = clickedItem.story.id;
          const storyScenes = scenes[storyId] || [];
          const sceneIdx = storyScenes.findIndex((s) => s.id === clickedItem.scene.id);
          setEditing({
            phase: "editing",
            storyId,
            sceneIndex: sceneIdx >= 0 ? sceneIdx : 0,
          });
        }
        return;
      }

      setFocusedIndex(index);
      const tier: NavTier = isSystemStory(clickedItem.story)
        ? "chuzi"
        : isStoryJettisoned(clickedItem.story)
          ? "jettisoned"
          : resolveStoryVisibilityTier(clickedItem.story);
      const tierIdx = mineNebulaNav.nebulaTiers.indexOf(tier);
      const tierStories = storiesByTier[tier] ?? [];
      const storyIdx = tierStories.findIndex((s) => s.id === clickedItem.story.id);
      if (tierIdx >= 0 && storyIdx >= 0) {
        setCosmosNav((prev) => ({
          ...prev,
          level: prev.level === "nebula" ? "story" : prev.level,
          nebulaIndex: tierIdx,
          storyIndex: storyIdx,
          sceneIndex: 0,
          choiceIndex: 0,
          breadcrumb:
            prev.level === "nebula"
              ? [{ level: "nebula", label: nebulaLabels[tierIdx] ?? tier, tierIndex: tierIdx }]
              : prev.breadcrumb,
        }));
      }
    } else {
      setFocusedIndex(index);
    }
  }, [
    placed,
    realm,
    scopeMode,
    storiesByTier,
    nebulaLabels,
    focusedIndex,
    scenes,
    cosmosNav.level,
    dispatchNav,
    mineNebulaNav.nebulaTiers,
  ]);

  const handleAllStorySelect = useCallback((index: number) => {
    setFocusedIndex(index);
    const story = allPlaced[index]?.story;
    if (!story) return;
    setAllCosmosNav((prev) => {
      const next = allCosmosNavReducer(
        prev,
        { type: "SELECT_STORY", storyId: story.id },
        allNavContext,
      );
      return next === prev ? prev : next;
    });
  }, [allPlaced, allNavContext]);

  function handleScopeChange(next: RealmScopeMode) {
    const path = realmScopePath(next);
    if (location.pathname !== path) {
      navigate(path);
    }
  }

  const handleSpaceAnchor = useCallback((anchor: [number, number, number]) => {
    setSpaceAnchor(anchor);
  }, []);

  const handleScenePositionChange = useCallback(
    (sceneId: string, position: [number, number, number]) => {
      setScenePositionOverrides((prev) => ({ ...prev, [sceneId]: position }));
    },
    [],
  );

  const handleSceneDragStart = useCallback((sceneId: string) => {
    setRearrangingSceneId(sceneId);
  }, []);

  const handleSceneDragEnd = useCallback((_sceneId: string) => {
    setRearrangingSceneId(null);
  }, []);

  const handleScenePositionCommit = useCallback(
    async (sceneId: string, position: [number, number, number]) => {
      const base = sceneBasePositionsRef.current.get(sceneId);
      if (!base) return;

      let clampedPosition = position;
      if (realm === "cosmos" && Array.isArray(stories)) {
        for (const story of stories) {
          const storyScenes = scenes[story.id];
          if (!storyScenes?.some((scene) => scene.id === sceneId)) continue;
          clampedPosition = clampPositionToTierNebula(
            position,
            resolveStoryVisibilityTier(story),
          );
          break;
        }
      }

      const layoutOffset = {
        x: clampedPosition[0] - base[0],
        y: clampedPosition[1] - base[1],
        z: clampedPosition[2] - base[2],
      };
      try {
        const updated = await api.scenes.update(sceneId, { layout_offset: layoutOffset });
        setScenes((prev) => {
          const next = { ...prev };
          for (const storyId of Object.keys(next)) {
            const idx = next[storyId].findIndex((s) => s.id === sceneId);
            if (idx >= 0) {
              next[storyId] = [...next[storyId]];
              next[storyId][idx] = updated;
              break;
            }
          }
          return next;
        });
      } catch (err) {
        console.error("[my-realm] failed to save scene layout offset", err);
      } finally {
        setScenePositionOverrides((prev) => {
          const next = { ...prev };
          delete next[sceneId];
          return next;
        });
      }
    },
    [realm, stories, scenes],
  );

  useEffect(() => {
    if (scopeMode === "all" && allPlaced.length > 0 && focusedIndex >= allPlaced.length) {
      setFocusedIndex(Math.max(0, allPlaced.length - 1));
    }
  }, [scopeMode, allPlaced.length, focusedIndex]);

  const focusedItem =
    scopeMode === "all"
      ? allPlaced.length > 0 && focusedIndex < allPlaced.length
        ? {
            story: allPlaced[focusedIndex].story,
            scene: {
              id: allPlaced[focusedIndex].story.id,
              story_id: allPlaced[focusedIndex].story.id,
              title: allPlaced[focusedIndex].story.title,
              order: 0,
              is_title: true as const,
              is_end: false as const,
              color: null,
              media_id: null,
              alt_media_id: null,
              media_mode: null,
              text_content: null,
              text_style: null,
              created_at: allPlaced[focusedIndex].story.created_at,
              updated_at: allPlaced[focusedIndex].story.updated_at,
            },
            visual: {
              ...cosmosMapping(allPlaced[focusedIndex].story),
              position: allPlaced[focusedIndex].anchor,
            },
            visibility: { navigable: true, dimmed: false },
          }
        : null
      : placed.length > 0 && focusedIndex < placed.length
        ? placed[focusedIndex]
        : null;
  const isFocusedStoryEditor = Boolean(
    focusedItem &&
      user &&
      (resolveStoryOwnerId(focusedItem.story) === user.id ||
        (user.is_admin && isSystemStory(focusedItem.story))),
  );
  const isFocusedStoryOwner = Boolean(
    focusedItem &&
      user &&
      resolveStoryOwnerId(focusedItem.story) === user.id &&
      !isSystemStory(focusedItem.story),
  );

  const scenesForEditingStory = useMemo(() => {
    if (editing.phase !== "editing") return [];
    return editorVisibleScenes(scenes[editing.storyId] || []);
  }, [editing, scenes]);

  useEffect(() => {
    if (!placed.length || focusedIndex >= placed.length) return;
    if (placed[focusedIndex].visibility.navigable) return;

    const nextNavigable = placed.findIndex((item) => item.visibility.navigable);
    if (nextNavigable >= 0 && nextNavigable !== focusedIndex) {
      setFocusedIndex(nextNavigable);
    }
  }, [placed, focusedIndex]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (creating.phase === "form" || creating.phase === "scenes") return;

      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      const isEditable = (e.target as HTMLElement)?.isContentEditable;
      const isInputFocused = tag === "input" || tag === "textarea" || tag === "select" || isEditable;

      if (editing.phase === "editing") {
        if (isInputFocused) return;
        if (e.key === "Escape") {
          e.preventDefault();
          setEditing({ phase: "idle" });
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setEditing((prev) => {
            if (prev.phase !== "editing") return prev;
            return { ...prev, sceneIndex: Math.max(0, prev.sceneIndex - 1) };
          });
          return;
        }
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setEditing((prev) => {
            if (prev.phase !== "editing") return prev;
            const max = (scenes[prev.storyId]?.length ?? 1) - 1;
            return { ...prev, sceneIndex: Math.min(max, prev.sceneIndex + 1) };
          });
          return;
        }
        return;
      }

      if (isInputFocused) return;

      if (scopeMode === "mine" && realm === "cosmos") {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          dispatchNav({ type: "LEFT" });
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          dispatchNav({ type: "RIGHT" });
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          dispatchNav({ type: "DOWN" });
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          dispatchNav({ type: "UP" });
        } else if (e.key === "Enter" && focusedItem && cosmosNav.level === "scene") {
          e.preventDefault();
          handleEnterEditMode();
        } else if (e.key === "Escape" && cosmosNav.level !== "nebula") {
          e.preventDefault();
          dispatchNav({ type: "UP" });
        }
        return;
      }

      if (scopeMode === "all" && realm === "cosmos") {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          dispatchAllNav({ type: "LEFT" });
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          dispatchAllNav({ type: "RIGHT" });
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          dispatchAllNav({ type: "DOWN" });
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          dispatchAllNav({ type: "UP" });
        } else if (e.key === "Enter" && focusedItem) {
          e.preventDefault();
          handleLaunchClick();
        }
        return;
      }

      const traversalItems = placed.length;
      if (!traversalItems) return;

      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIndex((cur) => {
          for (let next = cur + 1; next < placed.length; next++) {
            if (placed[next].visibility.navigable) return next;
          }
          return cur;
        });
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIndex((cur) => {
          for (let next = cur - 1; next >= 0; next--) {
            if (placed[next].visibility.navigable) return next;
          }
          return cur;
        });
      } else if (e.key === "Enter" && focusedItem && scopeMode === "mine") {
        e.preventDefault();
        handleEnterEditMode();
      }
    },
    [placed, scopeMode, creating, editing, scenes, focusedItem, realm, cosmosNav, dispatchNav, dispatchAllNav, handleLaunchClick]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  function handleEnterEditMode() {
    if (!focusedItem) return;
    const storyId = focusedItem.story.id;
    const storyScenes = scenes[storyId] || [];
    const sceneIndex = storyScenes.findIndex((s) => s.id === focusedItem.scene.id);
    setEditing({
      phase: "editing",
      storyId,
      sceneIndex: sceneIndex >= 0 ? sceneIndex : 0,
    });
  }

  const handleGeneratingChange = useCallback((sceneId: string, isGenerating: boolean) => {
    setGeneratingSceneIds((prev) => {
      const next = new Set(prev);
      if (isGenerating) {
        next.add(sceneId);
      } else {
        next.delete(sceneId);
      }
      return next;
    });
  }, []);

  function logout() {
    api.auth
      .logout()
      .catch(() => {})
      .finally(() => {
        auth.clearToken();
        beginOidcLogout();
      });
  }

  function handleMenuAction(action: DirectorMenuAction) {
    if (action === "logout") {
      logout();
      return;
    }
    if (action === "profile") {
      setOpenModal("profile");
    }
  }

  function handleCreateClick() {
    setCreationAnchor(
      computeNewStoryCreationAnchor(realm, stories, scenes, storyGraphs),
    );
    setCreating({ phase: "animating" });
  }

  async function handleLaunchClick() {
    if (scopeMode === "all") {
      const allItem = allPlaced[focusedIndex];
      if (!allItem) return;
      const storyId = allItem.story.id;
      if (!scenes[storyId]?.length) {
        try {
          const sceneList = await api.scenes.index(storyId);
          setScenes((prev) => ({ ...prev, [storyId]: sortScenesForFlow(sceneList) }));
        } catch {
          return;
        }
      }
      setViewing({ phase: "viewing", storyId });
      return;
    }
    if (!focusedItem) return;
    setViewing({ phase: "viewing", storyId: focusedItem.story.id });
  }

  function handlePublishClick() {
    if (focusedItem) {
      setStorySettingsMode("publish");
      setStorySettingsStoryId(focusedItem.story.id);
    }
  }

  function handleStoryPublishClick(storyId: string) {
    setStorySettingsMode("publish");
    setStorySettingsStoryId(storyId);
  }

  async function handleRetractOrUnpublish() {
    if (!focusedItem) return;
    const storyId = focusedItem.story.id;
    try {
      await api.stories.update(storyId, {
        published: false,
        soft_published: false,
      });

      const tier = mineNebulaNav.nebulaTiers[cosmosNav.nebulaIndex];
      const tierStories = tier ? (storiesByTier[tier] ?? []) : [];
      const remaining = tierStories.filter((s) => s.id !== storyId);
      if (remaining.length === 0) {
        setCosmosNav((prev) => ({
          ...prev,
          level: "nebula",
          storyIndex: 0,
          sceneIndex: 0,
          choiceIndex: 0,
          breadcrumb: [],
        }));
      } else {
        const nextIdx = Math.min(cosmosNav.storyIndex, remaining.length - 1);
        setCosmosNav((prev) => ({
          ...prev,
          level: "story",
          storyIndex: nextIdx,
          sceneIndex: 0,
          choiceIndex: 0,
        }));
      }

      handleStorySettingsSaved();
      setSpaceAnchor(null);
    } catch (err) {
      console.error("[my-realm] failed to retract/unpublish story", err);
    }
  }

  function handleRetractClick() {
    void handleRetractOrUnpublish();
  }

  function handleUnpublishClick() {
    void handleRetractOrUnpublish();
  }

  function handleStoryMetadataClick(storyId: string) {
    setStorySettingsMode("metadata");
    setStorySettingsStoryId(storyId);
  }

  function handleStorySettingsSaved(navigateToStoryId?: string) {
    setStorySettingsStoryId(null);
    setSpaceAnchor(null);
    api.stories
      .mine()
      .then((res: any) => {
        const data = extractStories(res);
        setStories(data);
        if (res?.meta?.coverboxes) {
          setCoverboxes(res.meta.coverboxes);
        }
        if (navigateToStoryId) {
          const story = data.find((s: StoryListItem) => s.id === navigateToStoryId);
          if (story) {
            const tier: NavTier = isSystemStory(story)
              ? "chuzi"
              : isStoryJettisoned(story)
                ? "jettisoned"
                : resolveStoryVisibilityTier(story);
            const idx = mineNebulaNav.nebulaTiers.indexOf(tier);
            if (idx >= 0) {
              setCosmosNav(initialNavState(idx));
            }
          }
        }
      })
      .catch(() => {});
  }

  function handleBirthSettled() {
    log("create:birth-settled", "→ form");
    setCreating({ phase: "form" });
  }

  async function ensureTitleScene(storyId: string, storyTitle: string) {
    try {
      const existing = await api.scenes.index(storyId);
      const hasTitle = existing.some((s) => s.is_title);

      let titleSceneId: string | undefined;
      if (!hasTitle) {
        log("create:ensureScenes", "creating title scene");
        const created = await api.scenes.create({ story_id: storyId, title: storyTitle, order: 0, is_title: true });
        titleSceneId = created.id;
      } else {
        titleSceneId = existing.find((s) => s.is_title)?.id;
      }

      if (titleSceneId) {
        try {
          const bgMediaId = await getSystemBackgroundMediaId();
          const directorName = user?.username ?? user?.name ?? "";
          const chosenByPrefix = t(realm, "chosen_by", "Chosen by");
          const creditsHtml = directorName
            ? `<p style="text-align:left">${chosenByPrefix} ${directorName}</p>`
            : "";

          const bgLine = {
            id: crypto.randomUUID(),
            type: "image" as const,
            media_id: bgMediaId,
            html: t(realm, "editor_bg_layer_label", "Background"),
            appear_at_ms: 0,
            fade_in_ms: 0,
            fade_out_ms: 0,
            duration_ms: 5000,
            width_pct: 100,
            height_pct: 100,
            position_x: 0,
            position_y: 0,
          };
          const creditLine = {
            id: crypto.randomUUID(),
            type: "credits" as const,
            html: creditsHtml,
            appear_at_ms: 0,
            fade_in_ms: 800,
            fade_out_ms: 800,
            duration_ms: 5000,
            width_pct: 30,
            height_pct: 10,
            position_x: 2,
            position_y: 88,
          };

          const lines = [bgLine, ...(creditsHtml ? [creditLine] : [])];
          const html = lines.filter((l) => l.type !== "image").map((l: any) => l.html).join("");

          await api.scenes.update(titleSceneId, {
            text_content: { html, lines },
            text_style: {
              font_family: "inherit",
              text_color: "#e8f0ff",
              background_color: "#04070d",
              font_size: 100,
            },
          });
        } catch (bgErr) {
          log("create:scaffoldTitleLayers", "ERROR", bgErr);
        }
      }

      const defaultName = t(realm, "untitled_scene");
      for (const scene of existing) {
        if (scene.title === "Start") {
          const rename = scene.is_title ? storyTitle : defaultName;
          await api.scenes.update(scene.id, { title: rename });
        }
      }
    } catch (err) {
      log("create:ensureScenes", "ERROR", err);
    }
  }

  function handleCreated(storyId: string, storyTitle: string = "Untitled") {
    log("create:handleCreated", { storyId, storyTitle, storiesBefore: stories?.length ?? null });
    setCreating({ phase: "scenes", storyId });

    ensureTitleScene(storyId, storyTitle).then(() => {
      log("create:fetch-mine", "started");
      return api.stories.mine();
    }).then((res: any) => {
      const keys = res != null && typeof res === "object" ? Object.keys(res) : [];
      log("create:fetch-mine", "resolved — keys:", keys.join(", "));
      const data = extractStories(res);
      const idx = data.findIndex((s) => s.id === storyId);
      log("create:fetch-mine", "setStories", {
        count: data.length,
        ids: data.map((s) => s.id),
        newStoryIndex: idx,
      });
      setStories(data);
      fetchScenesForStories(data);
      if (idx >= 0) {
        let sceneOffset = 0;
        for (let i = 0; i < idx; i++) {
          const sc = scenes[data[i].id];
          sceneOffset += sc ? editorVisibleScenes(sc).length : 1;
        }
        setSpaceAnchor(null);
        setFocusedIndex(sceneOffset);
        setTimeout(() => {
          setEditing({ phase: "editing", storyId, sceneIndex: 0 });
        }, 800);
      }
    }).catch((err: unknown) => {
      log("create:fetch-mine", "ERROR", err);
    });

    setTimeout(() => {
      log("create:timeout", "→ idle", { storiesLength: stories?.length ?? null });
      setCreating({ phase: "idle" });
    }, 4000);
  }

  function handleCreateCancel() {
    log("create:cancel");
    setCreating({ phase: "idle" });
  }

  async function handleDelete() {
    if (!focusedItem || deleting.phase !== "idle") return;
    const storyId = focusedItem.story.id;
    log("jettison:start", { storyId });
    try {
      await api.stories.jettison(storyId);
      setDeleting({ phase: "dying", storyId, mode: "jettison" });
    } catch (err: unknown) {
      log("jettison:api-error", err);
    }
  }

  async function handlePurgeJettisonedStory() {
    if (!focusedItem || deleting.phase !== "idle") return;
    const storyId = focusedItem.story.id;
    log("purge-jettisoned-story:start", { storyId });
    try {
      await api.stories.destroy(storyId);
      setDeleting({ phase: "dying", storyId, mode: "purge" });
    } catch (err: unknown) {
      log("purge-jettisoned-story:api-error", err);
    }
  }

  function handleDeathComplete() {
    if (deleting.phase !== "dying") return;
    const { storyId, mode } = deleting;
    log("death-complete", { storyId, mode });

    if (mode === "purge") {
      setStories((prev) => (prev ? prev.filter((s) => s.id !== storyId) : prev));
    } else {
      setStories((prev) =>
        prev
          ? prev.map((s) =>
              s.id === storyId ? { ...s, jettisoned_at: new Date().toISOString() } : s,
            )
          : prev,
      );
    }
    setFocusedIndex((i) => Math.max(0, i - 1));

    const tier = mineNebulaNav.nebulaTiers[cosmosNav.nebulaIndex];
    const tierStories = tier ? (storiesByTier[tier] ?? []) : [];
    const remaining = tierStories.filter((s) => s.id !== storyId);
    if (remaining.length === 0) {
      setCosmosNav((prev) => ({
        ...prev,
        level: "nebula",
        storyIndex: 0,
        sceneIndex: 0,
        choiceIndex: 0,
        breadcrumb: [],
      }));
    } else {
      const nextIdx = Math.min(cosmosNav.storyIndex, remaining.length - 1);
      setCosmosNav((prev) => ({
        ...prev,
        level: "story",
        storyIndex: nextIdx,
        sceneIndex: 0,
        choiceIndex: 0,
      }));
    }

    setDeleting({ phase: "idle" });
  }

  async function handlePromote() {
    if (!focusedItem) return;
    const storyId = focusedItem.story.id;
    log("promote:start", { storyId });
    try {
      await api.stories.unjettison(storyId);
      const recovered = { ...focusedItem.story, jettisoned_at: null };
      const privateStories = [...storiesByTier.private, recovered].sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      );
      const storyIdx = Math.max(0, privateStories.findIndex((s) => s.id === storyId));
      const nav: CosmosNavState = {
        level: "story",
        nebulaIndex: 0,
        storyIndex: storyIdx,
        sceneIndex: 0,
        choiceIndex: 0,
        breadcrumb: [
          {
            level: "nebula",
            label: nebulaLabels[0] ?? "private",
            tierIndex: 0,
          },
        ],
      };
      setStories((prev) =>
        prev
          ? prev.map((s) =>
              s.id === storyId ? { ...s, jettisoned_at: null } : s,
            )
          : prev,
      );
      setCosmosNav(nav);
      setSpaceAnchor(null);
      promoteSyncRef.current = true;
    } catch (err: unknown) {
      log("promote:api-error", err);
    }
  }

  async function handlePurgeJettisoned() {
    log("purge-jettisoned:start");
    try {
      await api.stories.purgeJettisoned();
      setStories((prev) =>
        prev ? prev.filter((s) => !s.jettisoned_at) : prev,
      );
    } catch (err: unknown) {
      log("purge-jettisoned:api-error", err);
    }
  }

  const isCreating = creating.phase !== "idle";
  const isDying = deleting.phase === "dying";
  const isEditing = editing.phase === "editing";
  const isViewing = viewing.phase === "viewing";
  const isStorySettingsOpen = storySettingsStoryId != null;
  const isAllMode = scopeMode === "all";
  const showHud =
    !isCreating &&
    !isDying &&
    !isEditing &&
    !isViewing &&
    !isStorySettingsOpen &&
    focusedItem != null &&
    (isAllMode || focusedItem.visibility.navigable) &&
    (realm === "cosmos" || hudScreen != null);

  const constellationControls = null;

  const deletingStoryId = deleting.phase === "dying" ? deleting.storyId : null;

  return (
    <div style={{ position: "relative", width: "100vw", height: "100vh", background: "#04070d" }}>
      {isAllMode ? (
        catalogLoading || (!catalogFull && !catalogError) ? (
          <CenterMessage>
            {realm === "wilds" ? "Loading the wilds…" : "Loading the cosmos…"}
          </CenterMessage>
        ) : catalogError ? (
          <CenterMessage tone="error">{catalogError}</CenterMessage>
        ) : realm === "cosmos" && catalogFull && !allCosmosLayoutReady ? (
          <CenterMessage>Charting constellations…</CenterMessage>
        ) : realm === "cosmos" && allCosmosLayout ? (
          <AllCosmosScene
            layout={{
              clusters: allCosmosLayout.tagLayout.clusters,
              storyPositions: allCosmosLayout.storyPositions,
            }}
            chuziCluster={allCosmosLayout.chuziCluster}
            chuziNebulaLabel={chuziNebulaLabel}
            visibleStories={allCosmosLayout.allStories}
            tagQuery={debouncedTagQuery}
            viewResetKey={`${debouncedTagQuery}|${catalogSortOrder}`}
            focusedIndex={focusedIndex}
            focusedStoryId={allFocusedStoryId}
            realm={realm}
            creatorAvatars={activeCatalogMeta?.creator_avatars ?? {}}
            coverboxes={activeCatalogMeta?.coverboxes ?? {}}
            scenes={scenes}
            sceneMaps={sceneMaps}
            storyGraphs={storyGraphs}
            onHudProject={setHudScreen}
            onSelect={handleAllStorySelect}
            navLevel={allCosmosNav.level}
            sceneIndex={allCosmosNav.sceneIndex}
            focusedClusterCenter={
              allNavContext.nebulaClusters[allCosmosNav.nebulaIndex]?.center ?? null
            }
            focusedClusterRadius={
              allNavContext.nebulaClusters[allCosmosNav.nebulaIndex]?.radius
            }
          />
        ) : (
          <CenterMessage>
            ALL view is available in cosmos realm.
          </CenterMessage>
        )
      ) : stories !== null ? (
        realm === "wilds" ? (
          <WildsScene
            placed={placed}
            focusedIndex={focusedIndex}
            creating={creating}
            creationAnchor={creationAnchor}
            editing={editing}
            deletingStoryId={deletingStoryId}
            generatingSceneIds={generatingSceneIds}
            storyGraphs={storyGraphs}
            sceneMaps={sceneMaps}
            coverboxes={coverboxes}
            onBirthSettled={handleBirthSettled}
            onDeathComplete={handleDeathComplete}
            onHudProject={setHudScreen}
            onSelect={handleSceneSelect}
          />
        ) : !cosmosLayoutReady ? (
          <CenterMessage>Charting constellations…</CenterMessage>
        ) : (
          <CosmosScene
            placed={placed}
            focusedIndex={focusedIndex}
            spaceAnchor={spaceAnchor}
            creating={creating}
            creationAnchor={creationAnchor}
            editing={editing}
            deletingStoryId={deletingStoryId}
            generatingSceneIds={generatingSceneIds}
            storyGraphs={storyGraphs}
            sceneMaps={sceneMaps}
            coverboxes={coverboxes}
            user={user}
            focusedControlsSlot={constellationControls}
            clusterResult={cosmosClusterResult}
            adminChuziCluster={adminChuziLayout?.cluster ?? null}
            chuziStoryPositions={adminChuziLayout?.storyPositions ?? null}
            adminChuziNebulaLabel={chuziNebulaLabel}
            mineBrowseMode={scopeMode === "mine"}
            storyBrowsePinned={cosmosNav.level === "story"}
            navLevel={cosmosNav.level}
            storyIndex={cosmosNav.storyIndex}
            sceneIndex={cosmosNav.sceneIndex}
            activeNebulaTier={mineNebulaNav.nebulaTiers[cosmosNav.nebulaIndex] ?? null}
            jettisonedAnchors={jettisonedAnchors}
            realm={realm}
            onStoryMetadataClick={handleStoryMetadataClick}
            onStoryPublishClick={handleStoryPublishClick}
            onBirthSettled={handleBirthSettled}
            onDeathComplete={handleDeathComplete}
            onHudProject={setHudScreen}
            onSelect={handleSceneSelect}
            onSpaceAnchor={handleSpaceAnchor}
            onScenePositionChange={handleScenePositionChange}
            onSceneDragStart={handleSceneDragStart}
            onSceneDragEnd={handleSceneDragEnd}
            onScenePositionCommit={handleScenePositionCommit}
            rearrangingSceneId={rearrangingSceneId}
            recenterKey={recenterKey}
          />
        )
      ) : error ? (
        <CenterMessage tone="error">{error}</CenterMessage>
      ) : (
        <CenterMessage>
          {realm === "wilds" ? "Loading the wilds…" : "Loading the cosmos…"}
        </CenterMessage>
      )}

      <DirectorMenu user={user} onAction={handleMenuAction} />

      {(!isCreating && !isEditing && !isViewing && !isAllMode) ? (
        <TagSearchBar
          realm={realm}
          value={tagQuery}
          onChange={(value) => {
            setTagQuery(value);
          }}
          showSort={false}
        />
      ) : null}

      {/* Radial nav — MINE mode (cosmos) */}
      {stories && !isCreating && !isEditing && !isViewing && !isAllMode && realm === "cosmos" ? (
        <CosmosRadialNav
          realm={realm}
          scopeMode={scopeMode}
          onScopeChange={handleScopeChange}
          onCreateClick={handleCreateClick}
          showCreate={!isAllMode}
          navLabel={currentNavLabel(cosmosNav, navContext)}
          navLevel={cosmosNav.level}
          navItemIndex={currentLevelIndex(cosmosNav)}
          navItemCount={currentLevelItemCount(cosmosNav, navContext)}
          canDrill={canDrillDown(cosmosNav, navContext)}
          onNavLeft={() => { setSpaceAnchor(null); dispatchNav({ type: "LEFT" }); }}
          onNavRight={() => { setSpaceAnchor(null); dispatchNav({ type: "RIGHT" }); }}
          onNavDrill={() => { setSpaceAnchor(null); dispatchNav({ type: "DOWN" }); }}
          onNavBack={() => { setSpaceAnchor(null); dispatchNav({ type: "UP" }); }}
          onNavCenter={() => { setSpaceAnchor(null); setRecenterKey((k) => k + 1); }}
          onNavKeyAction={handleEnterEditMode}
          showActions={showHud && !!focusedItem && (cosmosNav.level === "scene" || cosmosNav.level === "story")}
          keyAction="edit"
          canDelete={isFocusedStoryOwner}
          onDelete={isFocusedStoryOwner ? handleDelete : undefined}
          onPurge={isFocusedStoryOwner ? handlePurgeJettisonedStory : undefined}
          onPromote={isFocusedStoryOwner ? handlePromote : undefined}
          onEdit={handleEnterEditMode}
          onLaunch={isFocusedStoryEditor ? handleLaunchClick : undefined}
          onPublish={isFocusedStoryEditor ? handlePublishClick : undefined}
          onUnpublish={isFocusedStoryOwner ? handleUnpublishClick : undefined}
          onRetract={isFocusedStoryOwner ? handleRetractClick : undefined}
          storyTier={focusedItem ? resolveStoryVisibilityTier(focusedItem.story) : undefined}
          currentTier={mineNebulaNav.nebulaTiers[cosmosNav.nebulaIndex]}
          disabled={isDying}
        />
      ) : null}

      {/* Fallback: non-cosmos, pre-load, or ALL mode awaiting layout */}
      {!isCreating && !isEditing && !isViewing && !(stories && !isAllMode && realm === "cosmos") && !(isAllMode && realm === "cosmos" && allCosmosLayout) ? (
        <CosmosRadialNav
          realm={realm}
          scopeMode={scopeMode}
          onScopeChange={handleScopeChange}
          onCreateClick={handleCreateClick}
          showCreate={!isAllMode}
          navLabel=""
          navLevel="nebula"
          navItemIndex={0}
          navItemCount={0}
          canDrill={false}
          onNavLeft={() => {}}
          onNavRight={() => {}}
          onNavDrill={() => {}}
          onNavBack={() => {}}
          disabled={isDying}
        />
      ) : null}

      {/* Radial nav — ALL mode (cosmos) */}
      {!isCreating && !isEditing && !isViewing && isAllMode && realm === "cosmos" && allCosmosLayout ? (
        <CosmosRadialNav
          realm={realm}
          scopeMode={scopeMode}
          onScopeChange={handleScopeChange}
          onCreateClick={handleCreateClick}
          showCreate={false}
          navLabel={allCurrentNavLabel(allCosmosNav, allNavContext)}
          navLevel={allCosmosNav.level}
          navItemIndex={allCurrentLevelIndex(allCosmosNav)}
          navItemCount={allCurrentLevelItemCount(allCosmosNav, allNavContext)}
          canDrill={allCanDrillDown(allCosmosNav, allNavContext)}
          onNavLeft={() => dispatchAllNav({ type: "LEFT" })}
          onNavRight={() => dispatchAllNav({ type: "RIGHT" })}
          onNavDrill={() => dispatchAllNav({ type: "DOWN" })}
          onNavBack={() => dispatchAllNav({ type: "UP" })}
          onNavKeyAction={handleLaunchClick}
          disabled={isDying}
        />
      ) : null}

      {showHud && focusedItem && hudScreen && isAllMode && realm === "cosmos" && allCosmosNav.level !== "nebula" ? (
        <StoryHoverHud
          story={focusedItem.story}
          scene={focusedItem.scene}
          screenX={hudScreen.x}
          screenY={hudScreen.y}
          realm={realm}
          variant="controlsOnly"
          previewWidth={DEFAULT_CONSTELLATION_APPEARANCE.previewWidth}
          onLaunch={handleLaunchClick}
        />
      ) : null}

      {showHud && focusedItem && realm === "wilds" && hudScreen ? (
        <StoryHoverHud
          story={focusedItem.story}
          scene={focusedItem.scene}
          screenX={hudScreen.x}
          screenY={hudScreen.y}
          realm={realm}
          variant="controlsOnly"
          previewWidth={DEFAULT_CONSTELLATION_APPEARANCE.previewWidth}
          canDelete={isFocusedStoryOwner}
          onDelete={isFocusedStoryOwner ? handleDelete : undefined}
          onEdit={handleEnterEditMode}
          onLaunch={isFocusedStoryEditor ? handleLaunchClick : undefined}
          onPublish={isFocusedStoryEditor ? handlePublishClick : undefined}
          onUnpublish={isFocusedStoryOwner ? handleUnpublishClick : undefined}
          onRetract={isFocusedStoryOwner ? handleRetractClick : undefined}
        />
      ) : null}

      {isStorySettingsOpen && focusedItem && storySettingsStoryId === focusedItem.story.id ? (
        <PublishForm
          story={focusedItem.story}
          realm={realm}
          variant={storySettingsMode}
          onPublished={() => handleStorySettingsSaved(
            storySettingsMode === "publish" ? focusedItem.story.id : undefined
          )}
          onCancel={() => setStorySettingsStoryId(null)}
        />
      ) : null}

      {creating.phase === "form" ? (
        <CreateInline
          realm={realm}
          onCreated={handleCreated}
          onCancel={handleCreateCancel}
        />
      ) : null}

      {creating.phase === "animating" ? (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "rgba(232,240,255,0.5)",
            fontSize: 12,
            letterSpacing: 1,
            textTransform: "uppercase",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          {realm === "wilds" ? "Planting…" : "Igniting…"}
        </div>
      ) : null}

      {stories !== null && stories.length === 0 && creating.phase === "idle" ? (
        <div
          style={{
            position: "absolute",
            bottom: 80,
            left: "50%",
            transform: "translateX(-50%)",
            color: "rgba(232,240,255,0.55)",
            fontSize: 13,
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          {realm === "wilds"
            ? "Your wilds are empty. Plant your first tree."
            : "Your cosmos is empty. Birth your first star."}
        </div>
      ) : null}

      {openModal === "profile" ? (
        <DirectorProfileModal
          user={user}
          onClose={() => setOpenModal(null)}
          onUserUpdated={(u) => setUser(u)}
        />
      ) : null}

      {isViewing && viewing.phase === "viewing" ? (() => {
        const viewingStory =
          scopeMode === "all"
            ? catalogFullStories.find((s) => s.id === viewing.storyId)
            ?? catalogFiltered?.data.find((s) => s.id === viewing.storyId)
            : stories?.find((s) => s.id === viewing.storyId);
        if (!viewingStory) return null;
        const viewingStoryId = viewing.storyId;
        const viewingCoverbox =
          scopeMode === "all"
            ? activeCatalogMeta?.coverboxes[viewingStoryId] ?? null
            : coverboxes[viewingStoryId] ?? null;
        return (
          <StoryViewer
            story={viewingStory}
            scenes={scenes[viewing.storyId] ?? []}
            realm={realm}
            coverboxUrl={viewingCoverbox}
            onClose={() => {
              setViewing({ phase: "idle" });
              if (scopeMode === "mine") {
                const storyIdx = stories?.findIndex((s) => s.id === viewingStoryId) ?? -1;
                if (storyIdx >= 0) {
                  handleStorySelect(storyIdx);
                }
              }
            }}
          />
        );
      })() : null}

      {isEditing && editing.phase === "editing" ? (
        <SceneEditor
          story={stories?.find((s) => s.id === editing.storyId) ?? null}
          scenes={scenesForEditingStory}
          activeSceneIndex={editing.sceneIndex}
          realm={realm}
          coverboxUrl={coverboxes[editing.storyId] ?? null}
          user={user}
          onNavigate={(idx) => setEditing({ ...editing, sceneIndex: idx })}
          onClose={() => setEditing({ phase: "idle" })}
          onSceneUpdated={(updated) => {
            setScenes((prev) => {
              const storyScenes = prev[editing.storyId] ?? [];
              return {
                ...prev,
                [editing.storyId]: storyScenes.map((s) =>
                  s.id === updated.id ? updated : s,
                ),
              };
            });
          }}
          storySceneMap={sceneMaps[editing.storyId] ?? null}
          onScenesChanged={(nextScenes) => {
            setScenes((prev) => ({
              ...prev,
              [editing.storyId]: sortScenesForFlow(nextScenes),
            }));
            refreshStoryGraph(editing.storyId);
          }}
          onStoryStructureChange={() => refreshStoryGraph(editing.storyId, true)}
          onGeneratingChange={handleGeneratingChange}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Cosmos 3D scene                                                    */
/* ------------------------------------------------------------------ */

interface SceneProps {
  placed: PlacedScene[];
  focusedIndex: number;
  spaceAnchor?: [number, number, number] | null;
  creating: CreatingState;
  creationAnchor: [number, number, number];
  editing: EditingState;
  deletingStoryId: string | null;
  generatingSceneIds: Set<string>;
  storyGraphs: Record<string, TreeGraph>;
  sceneMaps: Record<string, Record<string, SceneMapEntry>>;
  coverboxes: Record<string, string | null>;
  user?: UserProfile | null;
  focusedControlsSlot?: ReactNode;
  clusterResult?: ClusterAnchorResult | null;
  adminChuziCluster?: { center: [number, number, number]; radius: number } | null;
  chuziStoryPositions?: Map<string, [number, number, number]> | null;
  adminChuziNebulaLabel?: string;
  mineBrowseMode?: boolean;
  storyBrowsePinned?: boolean;
  navLevel?: NavLevel;
  storyIndex?: number;
  sceneIndex?: number;
  activeNebulaTier?: NavTier | null;
  jettisonedAnchors?: Map<string, [number, number, number]>;
  realm?: RealmId;
  recenterKey?: number;
  onStoryMetadataClick?: (storyId: string) => void;
  onStoryPublishClick?: (storyId: string) => void;
  onBirthSettled: () => void;
  onDeathComplete: () => void;
  onHudProject: (pos: { x: number; y: number } | null) => void;
  onSelect: (index: number) => void;
  onSpaceAnchor?: (anchor: [number, number, number]) => void;
  onScenePositionChange?: (sceneId: string, position: [number, number, number]) => void;
  onSceneDragStart?: (sceneId: string) => void;
  onSceneDragEnd?: (sceneId: string) => void;
  onScenePositionCommit?: (sceneId: string, position: [number, number, number]) => void;
  rearrangingSceneId?: string | null;
}

function CosmosScene({
  placed,
  focusedIndex,
  spaceAnchor = null,
  creating,
  creationAnchor,
  editing,
  deletingStoryId,
  generatingSceneIds,
  storyGraphs,
  sceneMaps,
  coverboxes,
  user,
  focusedControlsSlot,
  clusterResult,
  adminChuziCluster = null,
  chuziStoryPositions = null,
  adminChuziNebulaLabel = "CHUZI!",
  mineBrowseMode = false,
  storyBrowsePinned = false,
  navLevel = "nebula",
  storyIndex = 0,
  sceneIndex = 0,
  activeNebulaTier = null,
  jettisonedAnchors = new Map(),
  realm: realmProp,
  recenterKey: recenterKeyProp = 0,
  onStoryMetadataClick,
  onStoryPublishClick,
  onBirthSettled,
  onDeathComplete,
  onHudProject,
  onSelect,
  onSpaceAnchor,
  onScenePositionChange,
  onSceneDragStart,
  onSceneDragEnd,
  onScenePositionCommit,
  rearrangingSceneId = null,
}: SceneProps) {
  const currentRealm = realmProp ?? "cosmos";
  const worldGroupRef = useRef<THREE.Group>(null);
  const spaceJetSyncRef = useRef<{ handleMiss: (e: MouseEvent) => void }>({
    handleMiss: () => {},
  });
  const jetEnabled =
    creating.phase === "idle" &&
    editing.phase !== "editing" &&
    !deletingStoryId &&
    !rearrangingSceneId &&
    !!onSpaceAnchor;
  const anchorPlaneY =
    placed.length > 0 && focusedIndex < placed.length
      ? placed[focusedIndex].visual.position[1]
      : 0;
  const focusedPlacement =
    placed.length > 0 && focusedIndex < placed.length
      ? placed[focusedIndex]
      : null;
  const focusedTier = activeNebulaTier ?? (focusedPlacement
    ? isSystemStory(focusedPlacement.story)
      ? "chuzi"
      : isStoryJettisoned(focusedPlacement.story)
        ? "jettisoned"
        : resolveStoryVisibilityTier(focusedPlacement.story)
    : null);
  const showStoryBrowseIndicator = mineBrowseMode && navLevel === "story";
  const showNebulaStoryLabels = mineBrowseMode && cosmosBrowseShowNebulaStoryLabels(navLevel);
  const hideClusterLabels = cosmosBrowseClusterLabelHidden(navLevel);
  const mineBrowseAppearance = useMemo(
    () =>
      mineBrowseMode
        ? cosmosBrowseAppearance(navLevel)
        : mergeConstellationAppearance({ showSceneLabels: false }),
    [mineBrowseMode, navLevel],
  );
  const focusedChoiceIds = focusedPlacement
    ? getOutgoingChoiceIds(
        storyGraphs[focusedPlacement.story.id],
        focusedPlacement.scene.id,
      )
    : [];
  const focusedStoryAnchor =
    focusedPlacement && clusterResult
      ? (chuziStoryPositions?.get(focusedPlacement.story.id) ??
        clusterResult.anchors.get(focusedPlacement.story.id) ??
        jettisonedAnchors.get(focusedPlacement.story.id) ??
        focusedPlacement.visual.position)
      : focusedPlacement && chuziStoryPositions
        ? (chuziStoryPositions.get(focusedPlacement.story.id) ??
          focusedPlacement.visual.position)
      : null;
  const activeCluster = useMemo(() => {
    if (activeNebulaTier === "chuzi" && adminChuziCluster) {
      return {
        center: adminChuziCluster.center,
        radius: adminChuziCluster.radius,
      };
    }
    if (!clusterResult || !activeNebulaTier) return null;
    if (activeNebulaTier === "jettisoned") {
      return JETTISON_NEBULA_CLUSTER;
    }
    if (
      activeNebulaTier === "private" ||
      activeNebulaTier === "limited" ||
      activeNebulaTier === "worldwide"
    ) {
      return clusterResult.clusters[activeNebulaTier];
    }
    return null;
  }, [clusterResult, activeNebulaTier, adminChuziCluster]);
  const mineFocusTarget = useMemo(() => {
    if (!activeCluster) return new THREE.Vector3(0, 0, 0);
    if (navLevel === "nebula") {
      return vec3FromTuple(activeCluster.center);
    }
    if ((navLevel === "scene" || navLevel === "choice") && focusedPlacement) {
      return vec3FromTuple(focusedPlacement.visual.position);
    }
    if (focusedStoryAnchor) {
      return vec3FromTuple(focusedStoryAnchor);
    }
    return vec3FromTuple(activeCluster.center);
  }, [navLevel, activeCluster, focusedStoryAnchor, focusedPlacement]);
  const worldNavPinned =
    mineBrowseMode &&
    !spaceAnchor &&
    creating.phase === "idle" &&
    editing.phase !== "editing" &&
    !rearrangingSceneId &&
    !!activeCluster;
  const focusEffect = DEFAULT_CONSTELLATION_APPEARANCE.focusEffect;

  return (
    <Canvas
      camera={{ position: [0, 6, 20], fov: 60, near: 0.1, far: 500 }}
      dpr={[1, 2]}
      gl={{ antialias: true }}
      style={{ width: "100%", height: "100%" }}
      onPointerMissed={(e) => spaceJetSyncRef.current.handleMiss(e)}
    >
      <SpaceJetNavigationSync
        syncRef={spaceJetSyncRef}
        enabled={jetEnabled}
        planeY={anchorPlaneY}
        onAnchor={onSpaceAnchor}
      />
      <color attach="background" args={["#04070d"]} />
      <ambientLight intensity={0.15} />
      <Stars
        radius={120}
        depth={60}
        count={3000}
        factor={4}
        fade
        saturation={0.4}
      />

      <group ref={worldGroupRef}>
      {adminChuziCluster ? (
        <>
          <ClusterNebula
            center={adminChuziCluster.center}
            radius={adminChuziCluster.radius}
            color={CHUZI_NEBULA_COLOR}
            opacity={focusedTier === "chuzi" ? 0.14 : 0.07}
            particleCount={focusedTier === "chuzi" ? 240 : 140}
            highlighted={focusedTier === "chuzi"}
          />
          <ClusterLabel
            position={adminChuziCluster.center}
            label={adminChuziNebulaLabel}
            color={CHUZI_NEBULA_COLOR}
            highlighted={focusedTier === "chuzi"}
            offsetY={adminChuziCluster.radius * 0.72}
            hidden={hideClusterLabels}
          />
        </>
      ) : null}

      {clusterResult && (
        <>
          <ClusterNebula
            center={clusterResult.clusters.private.center}
            radius={clusterResult.clusters.private.radius}
            color="#6b8cff"
            opacity={focusedTier === "private" ? 0.1 : 0.05}
            particleCount={focusedTier === "private" ? 220 : 120}
            highlighted={focusedTier === "private"}
          />
          <ClusterLabel
            position={clusterResult.clusters.private.center}
            label={t(currentRealm, "cosmos_cluster_unpublished")}
            color="#8ec8f8"
            highlighted={focusedTier === "private"}
            offsetY={clusterResult.clusters.private.radius * 0.72}
            hidden={hideClusterLabels}
          />
          <ClusterNebula
            center={clusterResult.clusters.limited.center}
            radius={clusterResult.clusters.limited.radius}
            color="#b07aff"
            opacity={focusedTier === "limited" ? 0.1 : 0.05}
            particleCount={focusedTier === "limited" ? 220 : 120}
            highlighted={focusedTier === "limited"}
          />
          <ClusterLabel
            position={clusterResult.clusters.limited.center}
            label={t(currentRealm, "cosmos_cluster_limited")}
            color="#c99aff"
            highlighted={focusedTier === "limited"}
            offsetY={clusterResult.clusters.limited.radius * 0.72}
            hidden={hideClusterLabels}
          />
          <ClusterNebula
            center={clusterResult.clusters.worldwide.center}
            radius={clusterResult.clusters.worldwide.radius}
            color="#f5a623"
            opacity={focusedTier === "worldwide" ? 0.1 : 0.05}
            particleCount={focusedTier === "worldwide" ? 220 : 120}
            highlighted={focusedTier === "worldwide"}
          />
          <ClusterLabel
            position={clusterResult.clusters.worldwide.center}
            label={t(currentRealm, "cosmos_cluster_published")}
            color="#f5c842"
            highlighted={focusedTier === "worldwide"}
            offsetY={clusterResult.clusters.worldwide.radius * 0.72}
            hidden={hideClusterLabels}
          />
          <ClusterNebula
            center={JETTISON_NEBULA_CENTER}
            radius={JETTISON_NEBULA_RADIUS}
            color="#ff5c5c"
            opacity={focusedTier === "jettisoned" ? 0.1 : 0.05}
            particleCount={focusedTier === "jettisoned" ? 220 : 120}
            highlighted={focusedTier === "jettisoned"}
          />
          <ClusterLabel
            position={JETTISON_NEBULA_CENTER}
            label={t(currentRealm, "cosmos_cluster_jettisoned")}
            color="#ff8a8a"
            highlighted={focusedTier === "jettisoned"}
            offsetY={JETTISON_NEBULA_RADIUS * 0.72}
            hidden={hideClusterLabels}
          />
        </>
      )}

      {(() => {
        const storyGroups = new Map<
          string,
          {
            story: StoryListItem;
            indices: number[];
            scenes: ConstellationSceneEntry[];
          }
        >();
        placed.forEach(({ story, scene, visual, visibility }, i) => {
          if (story.id === deletingStoryId) return;
          const isStoryOwner = Boolean(
            user?.id && resolveStoryOwnerId(story) === user.id,
          );
          const canEditStory =
            isStoryOwner || Boolean(user?.is_admin && isSystemStory(story));
          let group = storyGroups.get(story.id);
          if (!group) {
            group = { story, indices: [], scenes: [] };
            storyGroups.set(story.id, group);
          }
          group.indices.push(i);
          const isFocused = i === focusedIndex;
          const mapEntry = sceneMaps[story.id]?.[scene.id] ?? null;
          const coverboxUrl = coverboxes[story.id] ?? null;
          const previewContent: ReactNode | undefined = undefined;
          group.scenes.push({
            id: scene.id,
            isTitle: scene.is_title,
            isEnd: scene.is_end,
            visual,
            label: scene.is_title ? group.story.title : scene.title,
            labelOnly: showNebulaStoryLabels && scene.is_title,
            previewContent,
            dimmed: visibility.dimmed,
            locked: !visibility.navigable,
            focused: isFocused,
            controlsSlot: isFocused ? focusedControlsSlot : undefined,
            draggable:
              canEditStory &&
              editing.phase !== "editing" &&
              creating.phase === "idle",
          });
        });
        return Array.from(storyGroups.entries()).map(([storyId, group]) => {
          const edges: ConstellationEdgeEntry[] = (storyGraphs[storyId]?.edges ?? []).map(
            (edge) => ({
              source: edge.source,
              target: edge.target,
              type: edge.type,
            }),
          );
          const isStoryFocused =
            showStoryBrowseIndicator && group.indices.includes(focusedIndex);
          const isStoryOwner = Boolean(
            user?.id && resolveStoryOwnerId(group.story) === user.id,
          );
          const canEditStory =
            isStoryOwner || Boolean(user?.is_admin && isSystemStory(group.story));
          const systemStory = isSystemStory(group.story);
          const directorName = systemStory
            ? null
            : group.story.creator?.name ?? (isStoryOwner ? user?.name ?? null : null);
          const directorId = systemStory
            ? null
            : group.story.creator?.id ?? (isStoryOwner ? user?.id ?? null : null);
          const directorAvatarUrl =
            systemStory ? null : isStoryOwner ? user?.avatar_url ?? null : null;

          return (
            <Constellation
              key={`constellation-${storyId}`}
              published={resolveStoryVisibilityTier(group.story) === "worldwide"}
              storyFocused={isStoryFocused}
              showStoryTitle={isStoryFocused}
              storyTitle={group.story.title}
              storyOverlay={
                isStoryFocused
                  ? systemStory
                    ? {
                        overlayMode: "system",
                        publishedVersion: group.story.published_version ?? null,
                        publishedVersionLabel: t(currentRealm, "published_version_label"),
                        editable: canEditStory,
                        onEditClick: () => onStoryMetadataClick?.(storyId),
                        editAriaLabel: t("cosmos", "constellation_title_edit_hint"),
                        publishLabel: canEditStory
                          ? t("cosmos", "editor_publish_story")
                          : undefined,
                        onPublish: canEditStory
                          ? () => onStoryPublishClick?.(storyId)
                          : undefined,
                      }
                    : {
                      chosenByPrefix: t("cosmos", "chosen_by"),
                      directorName,
                      directorAvatarUrl,
                      directorAvatarFallback: directorId ? (
                        <DirectorAvatar seed={directorId} realm="cosmos" size={20} />
                      ) : undefined,
                      contentRating: group.story.content_rating,
                      genre: group.story.genre,
                      editable: isStoryFocused && canEditStory,
                      onEditClick: () => onStoryMetadataClick?.(storyId),
                      editAriaLabel: t("cosmos", "constellation_title_edit_hint"),
                      publishedVersion: group.story.published_version ?? null,
                      publishLabel: canEditStory ? t("cosmos", "editor_publish_story") : undefined,
                      onPublish: canEditStory ? () => onStoryPublishClick?.(storyId) : undefined,
                    }
                  : undefined
              }
              scenes={group.scenes}
              edges={edges}
              appearance={mineBrowseAppearance}
              showOverlays={editing.phase !== "editing"}
              onSceneSelect={(sceneIdx) => {
                if (!group.scenes[sceneIdx]?.locked) {
                  onSelect(group.indices[sceneIdx]);
                }
              }}
              onScenePositionChange={canEditStory ? onScenePositionChange : undefined}
              onSceneDragStart={canEditStory ? onSceneDragStart : undefined}
              onSceneDragEnd={canEditStory ? onSceneDragEnd : undefined}
              onScenePositionCommit={canEditStory ? onScenePositionCommit : undefined}
            />
          );
        });
      })()}

      {placed.map(({ story, visual }, i) =>
        story.id === deletingStoryId ? (
          <StarDeath
            key={`death-${story.id}-${i}`}
            position={visual.position}
            hue={210}
            onComplete={onDeathComplete}
          />
        ) : null
      )}

      {placed.map(({ scene, visual }) =>
        generatingSceneIds.has(scene.id) ? (
          <GeneratingEffect
            key={`gen-${scene.id}`}
            position={visual.position}
            color="#7eb8ff"
          />
        ) : null
      )}

      {focusedPlacement &&
        focusedChoiceIds.length > 0 &&
        creating.phase === "idle" &&
        !deletingStoryId &&
        editing.phase !== "editing" ? (
        <RealmChoiceVisual
          realm={currentRealm}
          center={focusedPlacement.visual.position}
          hue={focusedPlacement.visual.hue}
          choiceIds={focusedChoiceIds}
        />
      ) : null}

      {showStoryBrowseIndicator &&
        focusedStoryAnchor &&
        creating.phase === "idle" &&
        !deletingStoryId &&
        editing.phase !== "editing" ? (
        <StarFocusGlow
          position={focusedStoryAnchor}
          hue={focusedPlacement?.visual.hue ?? 210}
          intensity={focusedPlacement?.visual.intensity ?? 0.5}
          variant="browse"
        />
      ) : null}

      {(creating.phase === "animating" || creating.phase === "form") ? (
        <StarBirth
          position={creationAnchor}
          hue={210}
          onSettled={onBirthSettled}
        />
      ) : null}

      {creating.phase === "scenes" ? (
        <SceneOrbits center={creationAnchor} hue={210} />
      ) : null}
      </group>

      {worldNavPinned ? (
        <PinnedCosmosWorldMover
          groupRef={worldGroupRef}
          focusTarget={mineFocusTarget}
          focusKey={`${navLevel}|${mineFocusTarget.x},${mineFocusTarget.y},${mineFocusTarget.z}`}
          navLevel={navLevel}
          clusterRadius={activeCluster?.radius}
          pinnedCamera={pinnedCameraForMineLevel}
          onHudProject={onHudProject}
          hudWorldPosition={
            focusedPlacement
              ? vec3FromTuple(focusedPlacement.visual.position)
              : mineFocusTarget
          }
          hudOffsetY={DEFAULT_CONSTELLATION_APPEARANCE.previewOffsetY}
          hudScreenOffsetY={
            DEFAULT_CONSTELLATION_APPEARANCE.previewHeight * 0.48 + 4
          }
          initialCameraPos={[0, 6, 20]}
        />
      ) : null}

      <CameraController
        placed={placed}
        focusedIndex={focusedIndex}
        spaceAnchor={spaceAnchor}
        isCreating={creating.phase !== "idle"}
        creationAnchor={creationAnchor}
        editing={editing}
        onHudProject={onHudProject}
        worldNavPinned={worldNavPinned}
        viewLocked={!!rearrangingSceneId}
        recenterKey={recenterKeyProp}
      />
    </Canvas>
  );
}

/* ------------------------------------------------------------------ */
/*  Wilds 3D scene                                                     */
/* ------------------------------------------------------------------ */

function WildsScene({
  placed,
  focusedIndex,
  creating,
  creationAnchor,
  editing,
  deletingStoryId,
  generatingSceneIds,
  storyGraphs,
  sceneMaps: _sceneMaps,
  coverboxes: _coverboxes,
  onBirthSettled,
  onDeathComplete,
  onHudProject,
  onSelect,
}: SceneProps) {
  const focusedPlacement =
    placed.length > 0 && focusedIndex < placed.length
      ? placed[focusedIndex]
      : null;
  const focusedChoiceIds = focusedPlacement
    ? getOutgoingChoiceIds(
        storyGraphs[focusedPlacement.story.id],
        focusedPlacement.scene.id,
      )
    : [];

  return (
    <Canvas
      camera={{ position: [0, 8, 24], fov: 60, near: 0.1, far: 500 }}
      dpr={[1, 2]}
      gl={{ antialias: true }}
      style={{ width: "100%", height: "100%" }}
    >
      <color attach="background" args={["#0e1030"]} />
      <fog attach="fog" args={["#0e1030", 70, 180]} />
      <Stars
        radius={180}
        depth={80}
        count={2500}
        factor={4}
        saturation={0.5}
        fade
        speed={0.8}
      />
      <ambientLight intensity={0.65} />
      <hemisphereLight args={["#f0f8ff", "#3a5a38", 1.2]} />
      <directionalLight
        position={[10, 30, 8]}
        intensity={1.5}
        color="#fff5e0"
      />
      <Ground />
      <ForestBackdrop count={200} innerRadius={28} outerRadius={70} />

      {placed.map(({ story, scene, visual }, i) =>
        story.id === deletingStoryId ? (
          <TreeFall
            key={`${story.id}-${i}`}
            position={visual.position}
            hue={110}
            onComplete={onDeathComplete}
          />
        ) : (
          <Tree
            key={`${story.id}-${i}`}
            visual={visual}
            onSelect={() => onSelect(i)}
          />
        )
      )}

      {placed.map(({ scene, visual }) =>
        generatingSceneIds.has(scene.id) ? (
          <GeneratingEffect
            key={`gen-${scene.id}`}
            position={visual.position}
            color="#78c878"
            yOffset={0.02}
          />
        ) : null
      )}

      {focusedChoiceIds.length > 0 &&
        focusedPlacement &&
        creating.phase === "idle" &&
        !deletingStoryId &&
        editing.phase !== "editing" ? (
        <RealmChoiceVisual
          realm="wilds"
          center={focusedPlacement.visual.position}
          hue={focusedPlacement.visual.hue}
          choiceIds={focusedChoiceIds}
        />
      ) : null}

      {placed.length > 0 &&
        focusedIndex < placed.length &&
        creating.phase === "idle" &&
        !deletingStoryId ? (
        <StarFocusGlow
          position={placed[focusedIndex].visual.position}
          hue={placed[focusedIndex].visual.hue}
          intensity={placed[focusedIndex].visual.intensity}
          yOffset={0.02}
        />
      ) : null}

      {(creating.phase === "animating" || creating.phase === "form") ? (
        <TreeSprout
          position={creationAnchor}
          hue={110}
          onSettled={onBirthSettled}
        />
      ) : null}

      {creating.phase === "scenes" ? (
        <SceneBranches center={creationAnchor} hue={110} />
      ) : null}

      <CameraController
        placed={placed}
        focusedIndex={focusedIndex}
        isCreating={creating.phase !== "idle"}
        creationAnchor={creationAnchor}
        editing={editing}
        onHudProject={onHudProject}
        yOffset={4}
      />
    </Canvas>
  );
}

function Ground() {
  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -0.01, 0]}
      receiveShadow
    >
      <circleGeometry args={[120, 64]} />
      <meshStandardMaterial color="#2a5a35" roughness={0.9} />
    </mesh>
  );
}

/* ------------------------------------------------------------------ */
/*  Camera controller                                                  */
/* ------------------------------------------------------------------ */

const CAMERA_ZOOM_BACKOFF = 1.32;
const CAMERA_OVERVIEW_OFFSET: [number, number, number] = [0, 10, 34];
const CAMERA_ANCHOR_RELEASE_DISTANCE = 22;
const CAMERA_ANCHORED_MAX_DISTANCE = 28;
const CAMERA_FLOAT_MAX_DISTANCE = 90;
const CAMERA_JET_SLIDE_LERP = 0.0275;

function pointerToWorldOnPlane(
  event: MouseEvent,
  canvas: HTMLCanvasElement,
  camera: THREE.Camera,
  planeY: number,
): THREE.Vector3 | null {
  const rect = canvas.getBoundingClientRect();
  const ndcX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  const ndcY = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
  const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -planeY);
  const point = new THREE.Vector3();
  return raycaster.ray.intersectPlane(plane, point) ? point : null;
}

function SpaceJetNavigationSync({
  syncRef,
  enabled,
  planeY,
  onAnchor,
}: {
  syncRef: MutableRefObject<{ handleMiss: (e: MouseEvent) => void }>;
  enabled: boolean;
  planeY: number;
  onAnchor?: (anchor: [number, number, number]) => void;
}) {
  const { camera, gl } = useThree();

  syncRef.current.handleMiss = (event: MouseEvent) => {
    if (!enabled || !onAnchor || event.button !== 0) return;
    const point = pointerToWorldOnPlane(event, gl.domElement, camera, planeY);
    if (!point) return;
    onAnchor([point.x, point.y, point.z]);
  };

  return null;
}

function getOutgoingChoiceIds(
  graph: TreeGraph | undefined,
  sceneId: string,
): string[] {
  if (!graph) return [];
  return graph.edges
    .filter((edge) => edge.source === sceneId && edge.type === "choice")
    .map((edge) => edge.id);
}

function isPlaceholderScene(scene: SceneListItem): boolean {
  return scene.id.endsWith("-placeholder");
}

/** Pressure-zoom anchor: the focused star, not the full constellation bounding box. */
function computeStarCameraFrame(
  placed: PlacedScene[],
  focusedIndex: number,
  appearance: ConstellationAppearance,
) {
  if (focusedIndex >= placed.length) return null;

  const focused = placed[focusedIndex];
  const center = new THREE.Vector3(...focused.visual.position);
  const [ox, oy, oz] = appearance.cameraDefaultOffset;
  const offset = new THREE.Vector3(
    ox,
    oy * CAMERA_ZOOM_BACKOFF,
    oz * CAMERA_ZOOM_BACKOFF,
  );

  return {
    center,
    goalLookAt: center.clone(),
    goalPos: center.clone().add(offset),
    offset,
  };
}

type CameraMode = "transitioning" | "anchored" | "floating";

function CameraController({
  placed,
  focusedIndex,
  spaceAnchor = null,
  isCreating,
  creationAnchor = [0, 0, 0],
  editing,
  onHudProject,
  yOffset = 2,
  frameConstellation = false,
  worldNavPinned = false,
  viewLocked = false,
  recenterKey = 0,
}: {
  placed: PlacedScene[];
  focusedIndex: number;
  spaceAnchor?: [number, number, number] | null;
  isCreating: boolean;
  creationAnchor?: [number, number, number];
  editing: EditingState;
  onHudProject: (pos: { x: number; y: number } | null) => void;
  yOffset?: number;
  frameConstellation?: boolean;
  worldNavPinned?: boolean;
  viewLocked?: boolean;
  recenterKey?: number;
}) {
  const appearance = DEFAULT_CONSTELLATION_APPEARANCE;
  const { camera, size } = useThree();
  const targetPos = useRef(new THREE.Vector3(0, yOffset, 16));
  const targetLookAt = useRef(new THREE.Vector3(0, 0, 0));
  const mode = useRef<CameraMode>("transitioning");
  const controlsRef = useRef<any>(null);
  const prevFocusedIndex = useRef(focusedIndex);
  const prevSpaceAnchor = useRef(spaceAnchor);
  const prevIsCreating = useRef(isCreating);
  const prevEditingKey = useRef("");
  const prevFocusedWasPlaceholder = useRef(true);
  const viewOffset = useRef(
    new THREE.Vector3(...appearance.cameraDefaultOffset),
  );
  const lookOffset = useRef(
    new THREE.Vector3(...appearance.cameraTargetOffset),
  );
  const prevViewLocked = useRef(false);
  const prevRecenterKey = useRef(recenterKey);
  const frozenView = useRef<{
    pos: THREE.Vector3;
    target: THREE.Vector3;
  } | null>(null);

  const focusedItem =
    focusedIndex < placed.length ? placed[focusedIndex] : null;
  const focusedIsPlaceholder =
    focusedItem != null && isPlaceholderScene(focusedItem.scene);

  if (focusedIsPlaceholder !== prevFocusedWasPlaceholder.current) {
    if (!focusedIsPlaceholder) {
      mode.current = "transitioning";
    }
    prevFocusedWasPlaceholder.current = focusedIsPlaceholder;
  }

  if (focusedIndex !== prevFocusedIndex.current) {
    if (!focusedIsPlaceholder) {
      if (!worldNavPinned) {
        const frame = frameConstellation
          ? computeStarCameraFrame(placed, focusedIndex, appearance)
          : null;
        if (frame) {
          viewOffset.current.copy(frame.offset);
          lookOffset.current.set(0, 0, 0);
        } else {
          viewOffset.current.set(...appearance.cameraDefaultOffset);
          lookOffset.current.set(...appearance.cameraTargetOffset);
        }
      }
      mode.current = "transitioning";
    }
    prevFocusedIndex.current = focusedIndex;
  }

  const prevWorldNavPinned = useRef(worldNavPinned);
  if (worldNavPinned && !prevWorldNavPinned.current) {
    mode.current = "transitioning";
  }
  prevWorldNavPinned.current = worldNavPinned;

  if (recenterKey !== prevRecenterKey.current) {
    mode.current = "transitioning";
    prevRecenterKey.current = recenterKey;
  }

  const spaceAnchorChanged =
    spaceAnchor !== prevSpaceAnchor.current &&
    (spaceAnchor == null ||
      prevSpaceAnchor.current == null ||
      spaceAnchor[0] !== prevSpaceAnchor.current[0] ||
      spaceAnchor[1] !== prevSpaceAnchor.current[1] ||
      spaceAnchor[2] !== prevSpaceAnchor.current[2]);

  if (spaceAnchorChanged && spaceAnchor) {
    mode.current = "transitioning";
    viewOffset.current.set(...appearance.cameraDefaultOffset);
    lookOffset.current.set(...appearance.cameraTargetOffset);
    prevSpaceAnchor.current = spaceAnchor;
  } else if (spaceAnchor !== prevSpaceAnchor.current) {
    prevSpaceAnchor.current = spaceAnchor;
  }

  if (isCreating !== prevIsCreating.current) {
    mode.current = "transitioning";
    prevIsCreating.current = isCreating;
  }

  const isEditing = editing.phase === "editing";
  const editingKey = isEditing ? `${editing.storyId}-${editing.sceneIndex}` : "";
  if (editingKey !== prevEditingKey.current) {
    mode.current = "transitioning";
    prevEditingKey.current = editingKey;
  }

  const findEditingPosition = (): [number, number, number] | null => {
    if (!isEditing) return null;
    const storyScenes = placed.filter((p) => p.story.id === editing.storyId);
    const target = storyScenes[editing.sceneIndex];
    return target ? target.visual.position : null;
  };

  const starVector = (pos: [number, number, number]) =>
    new THREE.Vector3(pos[0], pos[1], pos[2]);

  const projectHud = (starPos: THREE.Vector3) => {
    const hudAnchor = starPos.clone();
    hudAnchor.y += appearance.previewOffsetY;
    hudAnchor.project(camera);
    const sx = (hudAnchor.x * 0.5 + 0.5) * size.width;
    const sy = (-hudAnchor.y * 0.5 + 0.5) * size.height;
    onHudProject({
      x: sx,
      y: sy + appearance.previewHeight * 0.48 + 4,
    });
  };

  useFrame(() => {
    const controls = controlsRef.current;

    if (viewLocked && !prevViewLocked.current) {
      frozenView.current = {
        pos: camera.position.clone(),
        target: (controls?.target ?? targetLookAt.current).clone(),
      };
    } else if (!viewLocked && prevViewLocked.current) {
      frozenView.current = null;
    }
    prevViewLocked.current = viewLocked;

    if (viewLocked && frozenView.current) {
      camera.position.copy(frozenView.current.pos);
      camera.lookAt(frozenView.current.target);
      if (controls) {
        controls.enabled = false;
        controls.target.copy(frozenView.current.target);
        controls.update();
      }
      if (placed.length > 0 && focusedIndex < placed.length) {
        projectHud(starVector(placed[focusedIndex].visual.position));
      }
      return;
    }

    if (worldNavPinned) {
      return;
    }

    let goalPos: THREE.Vector3;
    let goalLookAt: THREE.Vector3;

    const editPos = findEditingPosition();

    if (isCreating) {
      const anchor = starVector(creationAnchor);
      goalLookAt = anchor.clone().add(new THREE.Vector3(0, 1, 0));
      goalPos = anchor.clone().add(new THREE.Vector3(0, yOffset + 2, 12));
      onHudProject(null);
      mode.current = "transitioning";
    } else if (editPos) {
      goalLookAt = starVector(editPos);
      goalPos = goalLookAt
        .clone()
        .add(new THREE.Vector3(0, yOffset + 1.5, 8));
      onHudProject(null);
      mode.current = "transitioning";
    } else if (placed.length > 0 && focusedIndex < placed.length) {
      const focused = placed[focusedIndex];
      const starPos = starVector(focused.visual.position);
      const navCenter = spaceAnchor ? starVector(spaceAnchor) : starPos;
      const starFrame =
        !spaceAnchor && frameConstellation
          ? computeStarCameraFrame(placed, focusedIndex, appearance)
          : null;

      if (spaceAnchor) {
        goalLookAt = navCenter.clone();
        goalPos = navCenter.clone().add(viewOffset.current);
      } else if (focusedIsPlaceholder) {
        const [ox, oy, oz] = CAMERA_OVERVIEW_OFFSET;
        goalLookAt = starPos.clone();
        goalPos = starPos.clone().add(new THREE.Vector3(ox, oy, oz));
      } else if (starFrame) {
        goalLookAt = starFrame.goalLookAt;
        goalPos = starFrame.goalPos;
      } else {
        goalLookAt = starPos.clone().add(lookOffset.current);
        goalPos = starPos.clone().add(viewOffset.current);
      }

      projectHud(starPos);

      if (
        mode.current === "anchored" &&
        !focusedIsPlaceholder &&
        camera.position.distanceTo(navCenter) > CAMERA_ANCHOR_RELEASE_DISTANCE
      ) {
        mode.current = "floating";
      }
    } else {
      goalPos = new THREE.Vector3(0, yOffset + 4, 20);
      goalLookAt = new THREE.Vector3(0, 0, 0);
      onHudProject(null);
    }

    const orbitEnabled =
      !isCreating &&
      !isEditing &&
      mode.current !== "transitioning";

    if (controls) {
      controls.enabled = orbitEnabled;
      controls.minDistance = 2;
      controls.maxDistance =
        mode.current === "floating"
          ? CAMERA_FLOAT_MAX_DISTANCE
          : CAMERA_ANCHORED_MAX_DISTANCE;
    }

    if (mode.current === "transitioning" || isEditing) {
      const jetting = !!spaceAnchor && !isEditing;
      const lerp = isEditing
        ? 0.04
        : jetting
          ? CAMERA_JET_SLIDE_LERP
          : appearance.cameraSlideLerp;
      targetPos.current.lerp(goalPos, lerp);
      targetLookAt.current.lerp(goalLookAt, lerp);

      camera.position.copy(targetPos.current);
      camera.lookAt(targetLookAt.current);

      if (controls) {
        controls.target.copy(targetLookAt.current);
        controls.update();
      }

      if (
        !isEditing &&
        !focusedIsPlaceholder &&
        targetPos.current.distanceTo(goalPos) < appearance.cameraArrivalThreshold
      ) {
        mode.current = "anchored";
        const frame =
          !spaceAnchor && frameConstellation
            ? computeStarCameraFrame(placed, focusedIndex, appearance)
            : null;
        if (frame) {
          viewOffset.current.copy(targetPos.current).sub(frame.center);
          lookOffset.current.copy(targetLookAt.current).sub(frame.center);
        } else if (spaceAnchor) {
          viewOffset.current.copy(targetPos.current).sub(starVector(spaceAnchor));
          lookOffset.current.copy(targetLookAt.current).sub(starVector(spaceAnchor));
        }
        if (controls) {
          controls.target.copy(targetLookAt.current);
          controls.update();
        }
      }
    } else if (mode.current === "anchored") {
      if (placed.length > 0 && focusedIndex < placed.length) {
        const navCenter = spaceAnchor
          ? starVector(spaceAnchor)
          : starVector(placed[focusedIndex].visual.position);
        if (controls) {
          controls.target.lerp(navCenter, 0.12);
          controls.update();
        }
        projectHud(starVector(placed[focusedIndex].visual.position));
      }

      targetPos.current.copy(camera.position);
      targetLookAt.current.copy(controls ? controls.target : goalLookAt);
    } else {
      targetPos.current.copy(camera.position);
      targetLookAt.current.copy(controls ? controls.target : goalLookAt);

      if (placed.length > 0 && focusedIndex < placed.length) {
        projectHud(starVector(placed[focusedIndex].visual.position));
      }
    }
  });

  return worldNavPinned ? null : (
    <OrbitControls
      ref={controlsRef}
      enableDamping
      dampingFactor={0.08}
      minDistance={2}
      maxDistance={CAMERA_FLOAT_MAX_DISTANCE}
    />
  );
}

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                     */
/* ------------------------------------------------------------------ */

function CenterMessage({
  children,
  tone = "muted",
}: {
  children: React.ReactNode;
  tone?: "muted" | "error";
}) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: tone === "error" ? "#ff8888" : "rgba(232,240,255,0.65)",
        fontSize: 14,
      }}
    >
      {children}
    </div>
  );
}
